// App.jsx — PayTimer (plain React, no external UI libs)
// UT style: square borders, outlined fields, transparent backgrounds
// Light/Dark theme (persists), readable in both modes
// Features: hourly/daily/monthly/yearly (+ hours/week), currency, comma inputs
// Live money clock, Pause/Reset (zero flash), start date/time (seconds), weekend policy
  // Auto-reset: never/daily/weekly/monthly/yearly (scheduling only)
// Gift a Minute (Cash App) modal -> $gabrielramans

import React, { useEffect, useMemo, useRef, useState } from "react";
import { getCuratedEarningsForName } from "./famousData";

/* ---------------------------- small helpers ---------------------------- */
const STORAGE_KEY = "paytimer:self";
const THEME_KEY = "paytimer:theme";
const ZERO_FLASH_MS = 120;
const CASH_TAG = "gabrielramans";

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

function currentLocalISODate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
const TODAY_MAX = currentLocalISODate();

function nowHHMMSS() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function parseYMD(dateStr) {
  const [y, m, d] = (dateStr || currentLocalISODate()).split("-").map(Number);
  return { y, m: (m || 1) - 1, d: d || 1 };
}
function parseHMS(timeStr) {
  const parts = (timeStr || "00:00:00").split(":").map(Number);
  const [h = 0, mi = 0, s = 0] = parts.length === 2 ? [parts[0], parts[1], 0] : parts;
  return { h, mi, s };
}
function combineDateTimeLocal(dateStr, timeStr) {
  const { y, m, d } = parseYMD(dateStr);
  const { h, mi, s } = parseHMS(timeStr);
  return new Date(y, m, d, h, mi, s, 0);
}
function isWeekend(d) { const day = d.getDay(); return day === 0 || day === 6; }

function secondsBetweenWithWeekendPolicy(start, end, nonWorkConfig) {
  if (end <= start) return 0;
  // If no non-work constraints, count all time
  if (!nonWorkConfig?.enabled) return (end - start) / 1000;
  let total = 0; let cur = new Date(start);
  while (cur < end) {
    const dayEnd = new Date(cur); dayEnd.setHours(24, 0, 0, 0);
    const segEnd = dayEnd < end ? dayEnd : end;
    if (nonWorkConfig?.enabled) {
      const dow = cur.getDay();
      const safeDays = Array.isArray(nonWorkConfig.workDays) && nonWorkConfig.workDays.length === 7 ? nonWorkConfig.workDays : [false,false,false,false,false,false,false];
      if (!safeDays[dow]) {
        // skip full day if not a working day
      } else {
        // accumulate only within work hours window
        const [sh, sm] = String(nonWorkConfig.workStart || '09:00').split(":").map(n => parseInt(n || 0, 10));
        const [eh, em] = String(nonWorkConfig.workEnd || '17:00').split(":").map(n => parseInt(n || 0, 10));
        const workStart = new Date(cur); workStart.setHours(sh, sm, 0, 0);
        const workEnd = new Date(cur); workEnd.setHours(eh, em, 0, 0);
        const winStart = workStart < cur ? cur : workStart;
        const winEnd = workEnd < segEnd ? workEnd : segEnd;
        if (winEnd > winStart) total += (winEnd - winStart) / 1000;
      }
    } 
    cur = dayEnd;
  }
  return total;
}

function clampDay(y, m, dom) { const last = new Date(y, m + 1, 0).getDate(); return Math.min(dom, last); }
function mostRecentDaily(base, hhmm) {
  const [hh, mm] = (hhmm || "00:00").split(":").map(n => parseInt(n || 0, 10));
  const d = new Date(base); d.setSeconds(0, 0); d.setHours(hh || 0, mm || 0, 0, 0);
  if (d > base) d.setDate(d.getDate() - 1);
  return d;
}
function mostRecentWeekly(base, dow) {
  const d = new Date(base); d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - dow + 7) % 7; d.setDate(d.getDate() - diff);
  if (d > base) d.setDate(d.getDate() - 7);
  return d;
}
function mostRecentMonthly(base, dom) {
  const d = new Date(base); d.setHours(0, 0, 0, 0);
  const y = d.getFullYear(); const m = d.getMonth(); const target = clampDay(y, m, dom);
  let a = new Date(y, m, target, 0, 0, 0, 0);
  if (a > base) { const py = m === 0 ? y - 1 : y; const pm = m === 0 ? 11 : m - 1; a = new Date(py, pm, clampDay(py, pm, dom), 0, 0, 0, 0); }
  return a;
}
function mostRecentYearly(base, month1to12, dom) {
  const d = new Date(base); d.setHours(0, 0, 0, 0);
  const y = d.getFullYear(); const m0 = (month1to12 - 1); const target = clampDay(y, m0, dom);
  let a = new Date(y, m0, target, 0, 0, 0, 0);
  if (a > base) { const py = y - 1; a = new Date(py, m0, clampDay(py, m0, dom), 0, 0, 0, 0); }
  return a;
}
function resetAnchor(startDateStr, startTimeStr) {
  // Anchor is strictly the user-chosen start; auto-reset scheduling is handled separately
  return combineDateTimeLocal(startDateStr, startTimeStr);
}

function nextResetAfter(base, reset, schedule = {}) {
  const safe = { dailyResetTime: "00:00", weeklyResetDOW: 1, monthlyResetDOM: 1, yearlyResetMonth: 1, yearlyResetDay: 1, ...schedule };
  if (reset === 'never') return null;
  const b = new Date(base);
  if (reset === 'daily') {
    const [hh, mm] = (safe.dailyResetTime || '00:00').split(':').map(n=>parseInt(n||0,10));
    const d = new Date(b); d.setHours(hh||0, mm||0, 0, 0); if (d <= b) d.setDate(d.getDate()+1); return d;
  }
  if (reset === 'weekly') {
    const target = safe.weeklyResetDOW || 0; // 0=Sun
    const d = new Date(b); d.setHours(0,0,0,0);
    const diff = (target - d.getDay() + 7) % 7; d.setDate(d.getDate() + (diff === 0 ? 7 : diff)); return d;
  }
  if (reset === 'monthly') {
    const d = new Date(b); d.setHours(0,0,0,0);
    const nextMonth = new Date(d.getFullYear(), d.getMonth(), 1); nextMonth.setMonth(nextMonth.getMonth()+ (d.getDate() >= safe.monthlyResetDOM ? 1 : 0));
    const y = nextMonth.getFullYear(); const m = nextMonth.getMonth(); const day = clampDay(y, m, safe.monthlyResetDOM);
    return new Date(y, m, day, 0,0,0,0);
  }
  if (reset === 'yearly') {
    const y = b.getFullYear(); const mIdx = (safe.yearlyResetMonth||1)-1; const day = clampDay(y, mIdx, safe.yearlyResetDay||1);
    let d = new Date(y, mIdx, day, 0,0,0,0); if (d <= b) d = new Date(y+1, mIdx, clampDay(y+1, mIdx, safe.yearlyResetDay||1), 0,0,0,0); return d;
  }
  return null;
}

function secondsPer(unit) {
  const day = 86400;
  switch (unit) {
    case "daily": return day;
    case "monthly": return (365.25 / 12) * day;
    case "yearly": return 365.25 * day;
    default: return day;
  }
}

function computeActiveSecondsPerYear(options) {
  const day = 86400;
  const yearSeconds = 365.25 * day;
  const { includeWeekends, excludeNonWork, workDays = [], workStart = '09:00', workEnd = '17:00' } = options || {};
  if (excludeNonWork) {
    const safeDays = Array.isArray(workDays) && workDays.length === 7 ? workDays : [false,false,false,false,false,false,false];
    const [sh, sm] = String(workStart || '09:00').split(':').map(n => parseInt(n || 0, 10));
    const [eh, em] = String(workEnd || '17:00').split(':').map(n => parseInt(n || 0, 10));
    const span = Math.max(0, (eh*60+em) - (sh*60+sm)) * 60; // seconds per selected day
    const daysPerWeek = safeDays.reduce((a,b)=> a + (b ? 1 : 0), 0);
    const weekly = span * daysPerWeek;
    const yearly = weekly * 52;
    return Math.max(1, yearly);
  }
  if (includeWeekends) return yearSeconds;
  // exclude weekends only: 5 days per week
  return 5 * day * 52;
}
function useLocalStorageState(key, initial) {
  const [state, setState] = useState(() => {
    try { const raw = localStorage.getItem(key); if (!raw) return initial; const parsed = JSON.parse(raw); return { ...initial, ...parsed }; }
    catch { return initial; }
  });
  useEffect(() => { localStorage.setItem(key, JSON.stringify(state)); }, [key, state]);
  return [state, setState];
}
function formatMoney(value, currency) {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 6 }).format(value); }
  catch { return Number(value || 0).toFixed(2); }
}
function formatCompactCurrency(value, currency) {
  const abs = Math.abs(Number(value || 0));
  let num = abs; let suffix = '';
  if (abs >= 1_000_000_000) { num = abs / 1_000_000_000; suffix = 'B'; }
  else if (abs >= 1_000_000) { num = abs / 1_000_000; suffix = 'M'; }
  else if (abs >= 1_000) { num = abs / 1_000; suffix = 'K'; }
  const sym = currencySymbolFor(currency);
  const str = `${sym}${num.toFixed(num < 10 ? 2 : 1)}${suffix}`;
  return str;
}
function currencySymbolFor(code) {
  try {
    const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).formatToParts(0);
    const cur = parts.find(p => p.type === 'currency');
    return cur ? cur.value : '$';
  } catch { return '$'; }
}
// ---------- Taxes (progressive federal + state + city) ----------
// 2024 federal brackets (baseline). Values are taxable income thresholds.
const FEDERAL_STANDARD_DEDUCTION_2024 = {
  single: 14600,
  married: 29200,
  hoh: 21900,
};
const FEDERAL_BRACKETS_2024 = {
  single: [
    { upto: 11600, rate: 0.10 },
    { upto: 47150, rate: 0.12 },
    { upto: 100525, rate: 0.22 },
    { upto: 191950, rate: 0.24 },
    { upto: 243725, rate: 0.32 },
    { upto: 609350, rate: 0.35 },
    { upto: Infinity, rate: 0.37 },
  ],
  married: [
    { upto: 23200, rate: 0.10 },
    { upto: 94300, rate: 0.12 },
    { upto: 201050, rate: 0.22 },
    { upto: 383900, rate: 0.24 },
    { upto: 487450, rate: 0.32 },
    { upto: 731200, rate: 0.35 },
    { upto: Infinity, rate: 0.37 },
  ],
  hoh: [
    { upto: 16550, rate: 0.10 },
    { upto: 63100, rate: 0.12 },
    { upto: 100500, rate: 0.22 },
    { upto: 191950, rate: 0.24 },
    { upto: 243700, rate: 0.32 },
    { upto: 609350, rate: 0.35 },
    { upto: Infinity, rate: 0.37 },
  ],
};
// 2025 federal (approx): apply CPI-like scaling to 2024 thresholds; update standard deductions per IRS.
const FEDERAL_STANDARD_DEDUCTION_2025 = {
  single: 15000,
  married: 30000,
  hoh: 22500,
};
function scaleBrackets(base, factor) {
  return base.map((b) => ({ upto: isFinite(b.upto) ? Math.round(b.upto * factor) : Infinity, rate: b.rate }));
}
// Using the ratio of standard deductions (approx 2.74%) as an inflation proxy
const CPI_FACTOR_2025 = FEDERAL_STANDARD_DEDUCTION_2025.single / FEDERAL_STANDARD_DEDUCTION_2024.single; // ~1.0274
const FEDERAL_BRACKETS_2025 = {
  single: scaleBrackets(FEDERAL_BRACKETS_2024.single, CPI_FACTOR_2025),
  married: scaleBrackets(FEDERAL_BRACKETS_2024.married, CPI_FACTOR_2025),
  hoh: scaleBrackets(FEDERAL_BRACKETS_2024.hoh, CPI_FACTOR_2025),
};
function getFederalConfigForYear(year) {
  if (year === 2025) return { std: FEDERAL_STANDARD_DEDUCTION_2025, brackets: FEDERAL_BRACKETS_2025 };
  return { std: FEDERAL_STANDARD_DEDUCTION_2024, brackets: FEDERAL_BRACKETS_2024 };
}
function computeProgressiveTax(taxableIncome, brackets) {
  let remaining = Math.max(0, taxableIncome);
  let lower = 0;
  let tax = 0;
  for (const b of brackets) {
    const cap = Math.min(remaining, (isFinite(b.upto) ? (b.upto - lower) : remaining));
    if (cap <= 0) break;
    tax += cap * b.rate;
    remaining -= cap;
    lower = b.upto;
    if (remaining <= 0) break;
  }
  return tax;
}

// All US states + DC
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];
// Very rough, simplified effective income tax estimates (state + city). Not legal or financial advice.
const TAX_STATES = {
  'Alabama': { rate: 0.05, cities: {} },
  'Alaska': { rate: 0.00, cities: {} },
  'Arizona': { rate: 0.045, cities: {} },
  'California': { rate: 0.06, cities: { 'Los Angeles': 0.02, 'San Francisco': 0.015, 'San Diego': 0.012 } },
  'Colorado': { rate: 0.045, cities: {} },
  'Florida': { rate: 0.00, cities: { 'Miami': 0.01 } },
  'Georgia': { rate: 0.05, cities: {} },
  'Illinois': { rate: 0.0495, cities: { 'Chicago': 0.015 } },
  'Massachusetts': { rate: 0.05, cities: { 'Boston': 0.012 } },
  'New Jersey': { rate: 0.0637, cities: {} },
  'New York': { rate: 0.063, cities: { 'New York City': 0.03876, 'Buffalo': 0.01 } },
  'Oregon': { rate: 0.074, cities: { 'Portland': 0.0 } },
  'Pennsylvania': { rate: 0.0307, cities: { 'Philadelphia': 0.038, 'Pittsburgh': 0.03 } },
  'Texas': { rate: 0.00, cities: { 'Houston': 0.008, 'Austin': 0.008, 'Dallas': 0.008 } },
  'Washington': { rate: 0.00, cities: { 'Seattle': 0.01 } },
};
// State rules: flat or brackets (very simplified for demo).
const STATE_RULES = {
  AK: { type: 'none' }, FL: { type: 'none' }, NV: { type: 'none' }, SD: { type: 'none' }, TX: { type: 'none' }, WA: { type: 'none' }, WY: { type: 'none' }, TN: { type: 'none' }, NH: { type: 'none' },
  IL: { type: 'flat', rate: 0.0495 },
  PA: { type: 'flat', rate: 0.0307 },
  CO: { type: 'flat', rate: 0.044 },
  MA: { type: 'flat', rate: 0.05 },
  IN: { type: 'flat', rate: 0.03 }, // 2025
  IA: { type: 'flat', rate: 0.038 }, // 2025
  LA: { type: 'flat', rate: 0.03 }, // 2025
  MS: { type: 'flat', rate: 0.044 }, // 2025
  OR: { type: 'brackets', brackets: [ { upto: 4450, rate: 0.0475 }, { upto: 11150, rate: 0.0675 }, { upto: 125000, rate: 0.0875 }, { upto: Infinity, rate: 0.099 } ] },
  CA: { type: 'brackets', brackets: [ { upto: 10412, rate: 0.01 }, { upto: 24684, rate: 0.02 }, { upto: 38959, rate: 0.04 }, { upto: 54081, rate: 0.06 }, { upto: 68350, rate: 0.08 }, { upto: 349137, rate: 0.093 }, { upto: 418961, rate: 0.103 }, { upto: 698271, rate: 0.113 }, { upto: Infinity, rate: 0.123 } ] },
  NY: { type: 'brackets', brackets: [ { upto: 8500, rate: 0.04 }, { upto: 11700, rate: 0.045 }, { upto: 13900, rate: 0.0525 }, { upto: 21400, rate: 0.059 }, { upto: 80650, rate: 0.0597 }, { upto: 215400, rate: 0.0633 }, { upto: 1077550, rate: 0.0685 }, { upto: Infinity, rate: 0.109 } ] },
  NJ: { type: 'brackets', brackets: [ { upto: 20000, rate: 0.014 }, { upto: 35000, rate: 0.0175 }, { upto: 40000, rate: 0.035 }, { upto: 75000, rate: 0.05525 }, { upto: 500000, rate: 0.0637 }, { upto: 1000000, rate: 0.0897 }, { upto: Infinity, rate: 0.1075 } ] },
  GA: { type: 'flat', rate: 0.0549 },
  MI: { type: 'flat', rate: 0.0425 },
  OH: { type: 'brackets', brackets: [ { upto: 26050, rate: 0.0 }, { upto: 92150, rate: 0.0275 }, { upto: Infinity, rate: 0.0325 } ] },
};
function computeStateTaxAnnual(income, stateName) {
  if (!stateName) return 0;
  const state = US_STATES.find(s => s.name === stateName);
  const code = state?.code;
  const rule = code ? STATE_RULES[code] : null;
  if (!rule) {
    // fallback to previous simple estimate table if available
    const flat = TAX_STATES[stateName]?.rate || 0.05;
    return income * flat;
  }
  if (rule.type === 'none') return 0;
  if (rule.type === 'flat') return income * (rule.rate || 0);
  return computeProgressiveTax(income, rule.brackets || []);
}
// City income taxes (subset). Flat percentages.
const CITY_TAXES = {
  'New York': { 'New York City': 0.03876 },
  'Pennsylvania': { 'Philadelphia': 0.0379, 'Pittsburgh': 0.03 },
  'Michigan': { 'Detroit': 0.024, 'Grand Rapids': 0.0175 },
  'Ohio': { 'Columbus': 0.025, 'Cleveland': 0.025, 'Cincinnati': 0.018 },
  'Missouri': { 'St. Louis': 0.01, 'Kansas City': 0.01 },
  'Indiana': { 'Indianapolis': 0.0202 },
  'Alabama': { 'Birmingham': 0.01 },
};
// Compact day ranges like [false,true,true,true,true,true,false] => "Mon–Fri"
function compactDayRanges(daysBool) {
  const labels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const d = Array.isArray(daysBool) && daysBool.length === 7 ? daysBool : [false,false,false,false,false,false,false];
  const all = d.every(Boolean);
  if (all) return 'Daily';
  const res = [];
  let i = 0;
  while (i < 7) {
    if (!d[i]) { i++; continue; }
    const start = i;
    while (i + 1 < 7 && d[i + 1]) i++;
    const end = i;
    res.push(start === end ? labels[start] : `${labels[start]}–${labels[end]}`);
    i++;
  }
  return res.join(', ');
}
// input formatting with commas, preserving decimals while typing
function formatNumericWithCommas(valueStr) {
  if (valueStr === "") return "";
  const [intPart, decPart] = String(valueStr).split(".");
  const safeInt = intPart.replace(/[^0-9]/g, "");
  if (safeInt === "") return decPart ? `0.${decPart}` : "0";
  const formattedInt = Number(safeInt).toLocaleString();
  return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
}

// Choose a readable unit for a number of minutes
function bestUnitFromMinutes(totalMinutes) {
  const minutes = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const MIN_PER_HOUR = 60;
  const MIN_PER_DAY = 60 * 24;
  const MIN_PER_WEEK = MIN_PER_DAY * 7;
  if (minutes >= MIN_PER_WEEK) {
    const v = Math.max(1, Math.round(minutes / MIN_PER_WEEK));
    return { value: v, unit: v === 1 ? 'week' : 'weeks' };
  }
  if (minutes >= MIN_PER_DAY) {
    const v = Math.max(1, Math.round(minutes / MIN_PER_DAY));
    return { value: v, unit: v === 1 ? 'day' : 'days' };
  }
  if (minutes >= MIN_PER_HOUR) {
    const v = Math.max(1, Math.round(minutes / MIN_PER_HOUR));
    return { value: v, unit: v === 1 ? 'hour' : 'hours' };
  }
  return { value: minutes, unit: minutes === 1 ? 'minute' : 'minutes' };
}

// Convert minutes to the largest single unit (>=1) with one decimal, preferring years > months > weeks > days > hours
function formatLargestDecimalFromMinutes(totalMinutes) {
  const mins = Math.max(0, Number(totalMinutes) || 0);
  const minPerHour = 60;
  const minPerDay = 60 * 24;
  const minPerWeek = minPerDay * 7;
  const weeks = mins / minPerWeek;
  const years = weeks / 52;
  if (years >= 1) return `${years.toFixed(1)} years`;
  const months = weeks / 4;
  if (months >= 1) return `${months.toFixed(1)} months`;
  if (weeks >= 1) return `${weeks.toFixed(1)} weeks`;
  const days = mins / minPerDay;
  if (days >= 1) return `${days.toFixed(1)} days`;
  const hours = mins / minPerHour;
  if (hours >= 1) return `${hours.toFixed(1)} hours`;
  if (mins >= 1) return `${mins.toFixed(1)} minutes`;
  const seconds = mins * 60;
  return `${seconds.toFixed(1)} seconds`;
}

/* --------------------------- inline UT styles -------------------------- */
const S = {
  page: (dark) => ({
    minHeight: "100vh",
    background: dark ? "#0d0d0c" : "#fff",
    color: dark ? "#fff" : "#000",
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  }),
  header: {
    padding: 0,
    borderBottom: "1px solid rgba(0,0,0,0.2)"
  },
  headerInner: {
    maxWidth: 1040,
    margin: "0 auto",
    padding: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerMuted: (dark) => ({ fontSize: 12, color: dark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }),
  container: { maxWidth: 1200, margin: "0 auto", padding: "16px" },
  grid: { display: "grid", gap: 24, gridTemplateColumns: "1fr", },
  grid2: { display: "grid", gap: 24, gridTemplateColumns: "1fr 1fr" },
  card: (dark) => ({
    border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.2)"}`,
    background: dark
      ? 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.00))'
      : 'linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.00))',
    padding: 24,
    borderRadius: 2
  }),
  heroCard: (dark) => ({
    border: `1px solid ${dark ? '#292929' : 'rgba(0,0,0,0.2)'}`,
    background: dark
      ? 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.00))'
      : 'linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.00))',
    padding: 28,
    borderRadius: 2
  }),
  pulseWrap: {
    position: 'relative',
    width: 12,
    height: 12,
    marginRight: 6,
    flex: '0 0 auto'
  },
  pulseCore: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 8,
    height: 8,
    transform: 'translate(-50%, -50%)',
    borderRadius: 999,
    background: '#00db34',
    boxShadow: '0 0 10px rgba(0,219,52,0.6)',
    animation: 'paytimer-core 1.6s ease-in-out infinite'
  },
  pulseWave: (delay = '0s') => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 8,
    height: 8,
    borderRadius: 999,
    border: '2px solid #00db34',
    transform: 'translate(-50%, -50%) scale(0.4)',
    opacity: 0.6,
    animation: `paytimer-wave 1.8s ease-out ${delay} infinite`
  }),
  pulseDisabled: (dark) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 8,
    height: 8,
    transform: 'translate(-50%, -50%)',
    borderRadius: 999,
    background: dark ? '#777' : '#999'
  }),
  label: (dark) => ({ marginBottom: 6, fontSize: 13, color: dark ? "#fff" : "#000" }),
  input: (dark) => ({
    width: "100%", border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.25)"}`,
    background: "transparent", color: dark ? "#fff" : "#000", padding: "8px 12px", outline: "none", borderRadius: 2,
    boxSizing: "border-box", height: 40
  }),
  select: (dark) => ({
    width: "100%", border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.25)"}`,
    background: "transparent", color: dark ? "#fff" : "#000", padding: "8px 12px", outline: "none", borderRadius: 2,
    appearance: "none", boxSizing: "border-box", height: 40
  }),
  row: { display: "grid", gap: 16, gridTemplateColumns: "1fr" },
  row3: { display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr 1fr" },
  row2: { display: "grid", gap: 16, gridTemplateColumns: "1fr 1fr" },
  outlineBtn: (dark) => ({
    border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.4)"}`,
    background: "transparent", color: dark ? "#fff" : "#000", padding: "6px 12px", cursor: "pointer",
    borderRadius: 2
  }),
  iconBtn: (dark) => ({
    border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.4)"}`,
    background: "transparent",
    color: dark ? "#fff" : "#000",
    width: 32,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    borderRadius: 2,
    padding: 0
  }),
  greenBtn: {
    background: "#00CF31", color: "#fff", padding: "10px 18px", cursor: "pointer", border: "none", borderRadius: 2,
    fontWeight: 700, display: "flex", alignItems: "center", gap: 8, fontSize: 14
  },
  muted: (dark) => ({ fontSize: 12, color: dark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)" }),
  statBox: (dark) => ({
    border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.2)"}`,
    padding: 12, background: "transparent", borderRadius: 2, position: "relative",
    minWidth: 0
  }),
  durationValue: (dark) => ({
    fontFamily: S.monoBig.fontFamily,
    fontWeight: 700,
    fontSize: 16,
    color: dark ? "#fff" : "#000",
  }),
  inputLeftIconBtn: (dark) => ({
    position: 'absolute',
    left: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 2,
    border: 'none',
    background: 'transparent',
    color: dark ? '#fff' : '#000',
    cursor: 'pointer',
    opacity: 0.8,
  }),
  bigDuration: (dark) => ({
    fontFamily: S.monoBig.fontFamily,
    fontWeight: 800,
    fontSize: (typeof window !== 'undefined' && window.innerWidth < 600) ? 28 : 40,
    color: dark ? "#fff" : "#000",
  }),
  displayField: (dark, empty) => ({
    width: "100%",
    height: 40,
    boxSizing: "border-box",
    border: `1px solid ${dark ? "#292929" : "rgba(0,0,0,0.25)"}`,
    background: "transparent",
    borderRadius: 2,
    padding: "0 12px",
    display: "flex",
    alignItems: "center",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
    fontWeight: 700,
    fontSize: 16,
    color: empty ? (dark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)") : (dark ? "#fff" : "#000"),
    fontVariantNumeric: "tabular-nums",
    userSelect: "text",
  }),
  copyBtn: (dark) => ({
    position: "absolute",
    top: 6,
    right: 6,
    width: 20,
    height: 20,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    border: "none",
    background: "transparent",
    color: dark ? "#fff" : "#000",
    cursor: "pointer",
    opacity: 0.35,
  }),
  inputIconBtn: (dark) => ({
    position: "absolute",
    top: 8,
    right: 8,
    width: 24,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    border: "none",
    background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    color: dark ? "#fff" : "#000",
    cursor: "pointer",
    opacity: 0.6,
  }),
  inputSideBtn: (dark) => ({
    width: 28,
    height: 28,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 2,
    border: "none",
    background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
    color: dark ? "#fff" : "#000",
    cursor: "pointer",
    opacity: 0.6,
  }),
  inputOverlayBtn: (dark) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: 8,
    width: 24,
    height: 24,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 2,
    border: `1px solid ${dark ? '#292929' : 'rgba(0,0,0,0.25)'}`,
    background: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    color: dark ? '#fff' : '#000',
    cursor: 'pointer',
    opacity: 0.9,
    fontSize: 16,
  }),
  badge: (hexColor) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 2,
    border: '1px solid currentColor',
    color: hexColor,
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1.1,
    textTransform: 'none'
  }),
  // Circular preset selectors
  presetBtn: (dark, active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    height: 32,
    padding: '0 12px',
    cursor: 'pointer',
    borderRadius: 2,
    border: `1px solid ${active ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)')}`,
    background: active
      ? (dark
          ? 'linear-gradient(180deg, rgba(0,219,52,0.10), rgba(0,219,52,0.06))'
          : 'linear-gradient(180deg, rgba(0,219,52,0.10), rgba(0,219,52,0.05))')
      : (dark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
    color: dark ? '#fff' : '#000',
    whiteSpace: 'nowrap',
    flex: '1 1 auto',
    minWidth: 0,
    justifyContent: 'flex-start',
    transition: 'background 140ms ease, border-color 140ms ease, transform 120ms ease, box-shadow 140ms ease',
    userSelect: 'none',
    position: 'relative',
    overflow: 'hidden'
  }),
  presetCircle: (dark, active) => ({
    width: 14,
    height: 14,
    borderRadius: 999,
    border: `1px solid ${active ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.35)')}`,
    background: active ? '#00db34' : 'transparent',
    boxShadow: active ? 'inset 0 0 0 2px rgba(255,255,255,0.25)' : 'inset 0 0 0 2px rgba(0,0,0,0.18)',
    display: 'inline-block',
    aspectRatio: '1 / 1',
    flex: '0 0 auto',
    lineHeight: 0,
    transform: active ? 'scale(0.95)' : 'scale(1)',
    transition: 'transform 120ms ease'
  }),
  presetLabel: (dark) => ({ fontSize: 13, color: dark ? '#fff' : '#000', lineHeight: 1, whiteSpace: 'nowrap', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto' }),
  // presetAccent removed per design revert
  // Modern light/dark segmented toggle
  themeSegment: (dark) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0,
    border: `1px solid ${dark ? "#292929" : "#d0d0d0"}`,
    borderRadius: 2,
    overflow: "hidden",
    background: "transparent",
  }),
  themeOption: (dark, active) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 36,
    padding: 0,
    background: active ? (dark ? "#1b1b1b" : "#f2f2f2") : "transparent",
    color: dark ? "#fff" : "#000",
    border: "none",
    cursor: active ? "default" : "pointer",
    opacity: active ? 1 : 0.75,
    borderRadius: 2,
  }),
  // Header theme toggle (pill switch)
  themeTrack: (dark) => ({
    width: 56,
    height: 28,
    border: `1px solid ${dark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)"}`,
    background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
    position: "relative",
    cursor: "pointer",
    borderRadius: 2,
    padding: 0,
  }),
  themeThumb: (dark) => ({
    position: "absolute",
    top: 1,
    left: dark ? 28 : 1,
    width: 26,
    height: 26,
    background: dark ? "#000" : "#fff",
    color: dark ? "#fff" : "#000",
    borderRadius: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    boxShadow: dark ? "0 0 0 1px rgba(255,255,255,0.6)" : "0 0 0 1px rgba(0,0,0,0.6)",
    transition: "left 160ms ease",
  }),
  switchTrack: (dark, on) => ({
    width: 44,
    height: 22,
    border: `1px solid ${on ? '#00db34' : (dark ? '#444' : '#cfcfcf')}`,
    background: on ? '#00db34' : (dark ? '#2c2c2c' : '#e0e0e0'),
    position: 'relative',
    cursor: 'pointer',
    borderRadius: 2,
    padding: 0,
    outline: 'none',
  }),
  switchThumb: (dark, on) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    left: on ? 23 : 1,
    width: 18,
    height: 18,
    borderRadius: 2,
    background: '#ffffff',
    border: 'none',
    transition: 'left 160ms ease',
  }),
  switchText: (dark, on) => ({
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: on ? '#00db34' : (dark ? 'rgba(255,255,255,0.7)' : '#5a5a5a'),
  }),
  monoBig: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace", fontWeight: 600, fontSize: "48px" },
};

/* ---------------------------- small UI bits ---------------------------- */
function Stat({ label, value, dark, copyText, compact = false, containerStyle }) {
  const [copied, setCopied] = useState(false);
  async function handleCopy() {
    if (copyText === undefined) return;
    try { await navigator.clipboard.writeText(String(copyText)); setCopied(true); setTimeout(() => setCopied(false), 900); } catch {}
  }
  const hasCopy = copyText !== undefined;
  return (
    <div style={{ ...S.statBox(dark), padding: compact ? 6 : 12, ...(containerStyle || {}) }}>
      {copyText !== undefined && (
        <button type="button" aria-label="Copy value" onClick={handleCopy} style={S.copyBtn(dark)} title={copied ? 'Copied' : 'Copy'}>
          ⧉
        </button>
      )}
      <div style={{ ...S.muted(dark), fontSize: compact ? 10 : 12, textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: hasCopy ? 28 : 0 }}>{label}</div>
      <div
        style={{ color: dark ? "#fff" : "#000", fontSize: compact ? 12 : 14, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {String(value)}{copied ? ("  ✓") : ""}
      </div>
    </div>
  );
}

// Minimal stroke icons that inherit currentColor
function SunIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4 12H2M22 12h-2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}
function MoonIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}
function HeartIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 10-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z" />
    </svg>
  );
}
function PauseIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 5v14M16 5v14" />
    </svg>
  );
}
function PlayIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 5l12 7-12 7z" />
    </svg>
  );
}
function ResetIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.708" />
      <path d="M3 4v4h4" />
    </svg>
  );
}

function ArrowUpIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14" />
      <path d="M7 10l5-5 5 5" />
    </svg>
  );
}
function ArrowDownIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5" />
      <path d="M7 14l5 5 5-5" />
    </svg>
  );
}
function SortIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 17V7" />
      <path d="M5 10l3-3 3 3" />
      <path d="M16 7v10" />
      <path d="M13 14l3 3 3-3" />
    </svg>
  );
}
function GrokIcon({ size = 14 }) {
  const url = 'https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/dark/grok.png';
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        backgroundColor: 'currentColor',
        WebkitMaskImage: `url(${url})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        WebkitMaskPosition: 'center',
        maskImage: `url(${url})`,
        maskRepeat: 'no-repeat',
        maskSize: 'contain',
        maskPosition: 'center',
        borderRadius: 2,
      }}
    />
  );
}

function FieldLabel({ children, dark }) {
  return <div style={S.label(dark)}>{children}</div>;
}

function OutlineButton({ children, onClick, dark, style }) {
  return (
    <button onClick={onClick} style={{ ...S.outlineBtn(dark), ...style }}>
      {children}
    </button>
  );
}
function SolidGreenButton({ children, onClick, style }) {
  return (
    <button onClick={onClick} style={{ ...S.greenBtn, ...style }}>
      {children}
    </button>
  );
}

function PrefixedInput({ id, prefix, value, onChange, placeholder = "0", inputMode = "decimal", ariaLabel, dark, style }) {
  const ref = useRef(null);
  const [pad, setPad] = useState(28);
  useEffect(() => { if (ref.current) setPad(ref.current.offsetWidth + 12); }, [prefix]);
  const showPlaceholder = (value ?? "") === "";
  return (
    <div style={{ position: "relative", ...style }}>
      <span ref={ref} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: dark ? "#aaa" : "#666" }}>
        {prefix}
      </span>
      {showPlaceholder && (
        <span style={{ position: "absolute", left: pad, top: "50%", transform: "translateY(-50%)", color: dark ? "#888" : "#999", fontFamily: S.monoBig.fontFamily, fontSize: 14 }}>
          {placeholder}
        </span>
      )}
      <input
        id={id}
        inputMode={inputMode}
        aria-label={ariaLabel}
        type="text"
        value={value}
        onChange={onChange}
        style={{ ...S.input(dark), paddingLeft: pad }}
      />
    </div>
  );
}
function ZeroInput({ id, value, onChange, placeholder = "0", inputMode = "decimal", ariaLabel, dark, style }) {
  const show = (value ?? "") === "";
  return (
    <div style={{ position: "relative", ...style }}>
      {show && (
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: dark ? "#888" : "#999", fontFamily: S.monoBig.fontFamily, fontSize: 14 }}>
          {placeholder}
        </span>
      )}
      <input
        id={id}
        inputMode={inputMode}
        aria-label={ariaLabel}
        type="text"
        value={value}
        onChange={onChange}
        style={S.input(dark)}
      />
    </div>
  );
}

function SuffixInput({ id, suffix = "%", value, onChange, placeholder = "0", inputMode = "decimal", ariaLabel, dark, style }) {
  const ref = useRef(null);
  const [padRight, setPadRight] = useState(24);
  useEffect(() => { if (ref.current) setPadRight(ref.current.offsetWidth + 12); }, [suffix]);
  const show = (value ?? "") === "";
  return (
    <div style={{ position: "relative", ...style }}>
      {show && (
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: dark ? "#888" : "#999", fontFamily: S.monoBig.fontFamily, fontSize: 14 }}>
          {placeholder}
        </span>
      )}
      <input
        id={id}
        inputMode={inputMode}
        aria-label={ariaLabel}
        type="text"
        value={value}
        onChange={onChange}
        style={{ ...S.input(dark), paddingRight: padRight }}
      />
      <span ref={ref} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: dark ? "#aaa" : "#666" }}>
        {suffix}
      </span>
    </div>
  );
}

function AllocSlider({ value, onChange, dark, inputId }) {
  const percentValue = clamp(Number(value) || 0, 0, 100);
  const handleRangeChange = (e) => {
    const v = clamp(Number(e.target.value) || 0, 0, 100);
    onChange(v);
  };
  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    const v = clamp(Number(raw) || 0, 0, 100);
    onChange(v);
  };
  const borderColor = dark ? '#292929' : 'rgba(0,0,0,0.25)';
  const trackBg = dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
  const fillGradient = 'linear-gradient(90deg, #00db34, #06c43a)';
  const handleW = 2;
  const handleH = 16;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 12 }}>
      <div style={{ position: 'relative', height: 24 }}>
        <div style={{
          position: 'absolute', top: '50%', left: 0, right: 0, height: 10,
          transform: 'translateY(-50%)', borderRadius: 2, overflow: 'hidden',
          background: trackBg, border: `1px solid ${borderColor}`
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: 0, height: 10,
          transform: 'translateY(-50%)', borderRadius: 2,
          width: `${percentValue}%`, background: fillGradient,
        }} />
        <div style={{
          position: 'absolute', top: '50%', left: `calc(${percentValue}% - ${handleW / 2}px)`,
          width: handleW, height: handleH, transform: 'translateY(-50%)',
          background: '#00db34', borderRadius: 2
        }} />
        <input type="range" min={0} max={100} step={1} value={percentValue}
          onChange={handleRangeChange}
          style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'ew-resize' }}
          aria-label="Allocate percent slider"
        />
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <input
          id={inputId || 'allocPercent'}
          inputMode="numeric"
          aria-label="Allocate percent"
          type="text"
          value={String(percentValue)}
          onChange={handleInputChange}
          style={{
            width: 42,
            textAlign: 'right',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: dark ? '#fff' : '#000',
            fontFamily: S.monoBig.fontFamily,
            fontWeight: 700,
            fontSize: 14,
          }}
        />
        <span style={{ color: dark ? '#bbb' : '#666', fontSize: 14, fontWeight: 600 }}>%</span>
      </div>
    </div>
  );
}

/* --------------------------------- App --------------------------------- */
const defaultState = {
  amount: "",
  unit: "monthly",
  currency: "USD",
  startDate: currentLocalISODate(),
  startTime: "00:00:00",
  reset: "never",
  includeWeekends: true,
  excludeNonWork: false,
  workDays: [false, true, true, true, true, true, false],
  workStart: "09:00",
  workEnd: "17:00",
  hourlyRate: "",
  hoursPerWeek: "",
  dailyResetTime: "00:00",
  weeklyResetDOW: 1,
  monthlyResetDOM: 1,
  yearlyResetMonth: 1,
  yearlyResetDay: 1,
};

export default function App() {
  // Theme
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "light");
  const dark = theme === "dark";
  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
    try {
      const root = document.documentElement;
      root.setAttribute('data-theme', dark ? 'dark' : 'light');
    } catch {}
  }, [theme]);

  const [state, setState] = useLocalStorageState(STORAGE_KEY, defaultState);
  const {
    amount, unit, currency, startDate, startTime, reset, includeWeekends,
    excludeNonWork, workDays, workStart, workEnd,
    dailyResetTime, weeklyResetDOW, monthlyResetDOM, yearlyResetMonth, yearlyResetDay,
    hourlyRate, hoursPerWeek
  } = state;
  const [startPreset, setStartPreset] = useState('custom');
  const startFieldsRef = useRef(null);
  const [startFieldsMeasuredH, setStartFieldsMeasuredH] = useState(0);
  useEffect(() => {
    if (startPreset === 'custom' && startFieldsRef.current) {
      const h = startFieldsRef.current.getBoundingClientRect().height;
      if (h && h !== startFieldsMeasuredH) setStartFieldsMeasuredH(h);
    }
  }, [startPreset, dark, unit, currency, startDate, startTime]);

  // live clock
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => { let raf = 0; const tick = () => { setNowMs(Date.now()); raf = requestAnimationFrame(tick); }; raf = requestAnimationFrame(tick); return () => cancelAnimationFrame(raf); }, []);
  const now = useMemo(() => new Date(nowMs), [nowMs]);

  // Guard future start dates
  useEffect(() => { if (startDate > TODAY_MAX) setState(s => ({ ...s, startDate: TODAY_MAX })); }, [startDate, setState]);

  // currency symbol + comma display for inputs
  const currencySymbol = useMemo(() => currencySymbolFor(currency), [currency]);
  const displayAmount = useMemo(() => formatNumericWithCommas(amount), [amount]);
  const displayHourlyRate = useMemo(() => formatNumericWithCommas(hourlyRate), [hourlyRate]);

  // formatters
  const formatter = useMemo(() => new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 6 }), [currency]);
  const sixFormatter = useMemo(() => new Intl.NumberFormat(undefined, { style: "currency", currency, minimumFractionDigits: 6, maximumFractionDigits: 6 }), [currency]);

  // rate per second
  const annualIncome = useMemo(() => {
    if (unit === 'hourly') {
      const hr = Number(hourlyRate) || 0;
      const hw = Number(hoursPerWeek) || 0;
      return hr * hw * 52;
    }
    if (unit === 'monthly') return (Number(amount) || 0) * 12;
    if (unit === 'yearly') return (Number(amount) || 0);
    // daily
    return (Number(amount) || 0) * 365.25;
  }, [unit, amount, hourlyRate, hoursPerWeek]);

  const activeSecondsYearMemo = useMemo(() => computeActiveSecondsPerYear({ includeWeekends: true, excludeNonWork, workDays, workStart, workEnd }), [excludeNonWork, workDays, workStart, workEnd]);

  const perSecond = useMemo(() => {
    const base = activeSecondsYearMemo > 0 ? (annualIncome / activeSecondsYearMemo) : 0;
    return base;
  }, [annualIncome, activeSecondsYearMemo]);

  // anchors & elapsed
  const anchor = useMemo(() => resetAnchor(startDate, startTime), [startDate, startTime]);

  const [paused, setPaused] = useState(false);
  const [pauseStartedMs, setPauseStartedMs] = useState(null);
  // Stable planned reset timestamp (ms since epoch)
  const [scheduledResetTs, setScheduledResetTs] = useState(null);
  // Recompute planned reset when settings change
  useEffect(() => {
    if (reset === 'never') { setScheduledResetTs(null); return; }
    const next = nextResetAfter(new Date(), reset, { dailyResetTime, weeklyResetDOW, monthlyResetDOM, yearlyResetMonth, yearlyResetDay });
    setScheduledResetTs(next ? next.getTime() : null);
  }, [reset, dailyResetTime, weeklyResetDOW, monthlyResetDOM, yearlyResetMonth, yearlyResetDay]);
  // Fire when we pass the planned time, then plan the subsequent one
  useEffect(() => {
    if (reset === 'never' || !scheduledResetTs) return;
    if (nowMs >= scheduledResetTs) {
      handleReset();
      const next = nextResetAfter(new Date(scheduledResetTs + 1000), reset, { dailyResetTime, weeklyResetDOW, monthlyResetDOM, yearlyResetMonth, yearlyResetDay });
      setScheduledResetTs(next ? next.getTime() : null);
    }
  }, [nowMs, scheduledResetTs, reset, dailyResetTime, weeklyResetDOW, monthlyResetDOM, yearlyResetMonth, yearlyResetDay]);
  const effectiveEnd = paused && pauseStartedMs ? new Date(pauseStartedMs) : now;
  const elapsedSec = useMemo(() => secondsBetweenWithWeekendPolicy(
    anchor,
    effectiveEnd,
    excludeNonWork ? { enabled: true, workDays, workStart, workEnd } : { enabled: false }
  ), [anchor, effectiveEnd, excludeNonWork, workDays, workStart, workEnd]);
  const liveValue = perSecond * elapsedSec;

  // zero flash
  const [zeroFlash, setZeroFlash] = useState(false);
  const [showZeroWhilePaused, setShowZeroWhilePaused] = useState(false);

  // gift modal
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftAmount, setGiftAmount] = useState("");
  useEffect(() => { if (giftOpen) { const amt = Math.max(0, Number((perSecond * 60).toFixed(2)) || 0); setGiftAmount(amt.toFixed(2)); } }, [giftOpen, perSecond]);
  const [donutHover, setDonutHover] = useState('none');
  // Taxes section ref for smooth scroll
  const taxesRef = useRef(null);
  const [taxBadgeHover, setTaxBadgeHover] = useState(false);
  // Rotating period for taxes caption under Effective rate
  const PERIODS = useMemo(() => ([
    { label: '1 hour', minutes: 60 },
    { label: '1 day', minutes: 60 * 24 },
    { label: '1 week', minutes: 60 * 24 * 7 },
    { label: '1 year', minutes: Math.round(60 * 24 * 365.25) },
    { label: '10 years', minutes: Math.round(60 * 24 * 365.25 * 10) },
    { label: '1 minute', minutes: 1 },
  ]), []);
  const [periodIdx, setPeriodIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setPeriodIdx((i) => (i + 1) % PERIODS.length), 5000);
    return () => clearInterval(id);
  }, [PERIODS.length]);
  const currentPeriod = PERIODS[periodIdx];
  // Responsive columns for the taxes 100-square grid
  const taxGridWrapRef = useRef(null);
  const [taxCols, setTaxCols] = useState(73);
  useEffect(() => {
    const el = taxGridWrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const isMobile = (typeof window !== 'undefined' && window.innerWidth < 640);
    const gap = isMobile ? 2 : 3; // compact spacing on mobile
    const minSquare = isMobile ? 4 : 7; // keep squares small but readable on mobile
    const ro = new ResizeObserver((entries) => {
      const measuredWidth = entries[0]?.contentRect?.width || el.clientWidth || 0;
      const width = Math.max(0, Math.floor(measuredWidth) - 2); // bias inward to prevent edge overlap
      // Only choose column counts that divide 365 exactly to guarantee a perfect rectangle
      // Divisors of 365: 1, 5, 73, 365. Prefer 73 (5 rows) to match the mock.
      const divisors = isMobile ? [73, 365, 5, 1] : [73, 365, 5, 1];
      let chosen = 73;
      for (const cols of divisors) {
        const cell = (width - gap * (cols - 1)) / cols;
        if (cell >= minSquare) { chosen = cols; break; }
      }
      setTaxCols(chosen);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Native picker helpers for date/time inputs
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);
  function openNativePicker(ref) {
    try { if (ref.current && typeof ref.current.showPicker === 'function') { ref.current.showPicker(); return; } } catch {}
    try { ref.current?.focus(); } catch {}
  }

  /* ------------------------------ handlers ----------------------------- */
  function setStartToNowAndPresetCustom() {
    const ymd = currentLocalISODate();
    const hms = nowHHMMSS();
    setStartPreset('now');
    setState(s => ({ ...s, startDate: ymd, startTime: hms }));
  }
  function handleAmount(e) { const v = e.target.value.replace(/[^0-9.]/g, ""); setState(s => ({ ...s, amount: v })); setStartToNowAndPresetCustom(); }
  function handleHourlyRate(e) { const v = e.target.value.replace(/[^0-9.]/g, ""); setState(s => ({ ...s, hourlyRate: v })); setStartToNowAndPresetCustom(); }
  function handleHoursPerWeek(e) { const raw = e.target.value.replace(/[^0-9.]/g, ""); const num = clamp(Number(raw) || 0, 0, 168); setState(s => ({ ...s, hoursPerWeek: String(num) })); setStartToNowAndPresetCustom(); }

  function handlePauseToggle() {
    if (!paused) { setPaused(true); setPauseStartedMs(Date.now()); return; }
    const deltaMs = Date.now() - (pauseStartedMs || Date.now());
    const startDT = combineDateTimeLocal(startDate, startTime);
    const newStart = new Date(startDT.getTime() + deltaMs);
    const y = newStart.getFullYear(); const m = String(newStart.getMonth() + 1).padStart(2, '0'); const d = String(newStart.getDate()).padStart(2, '0');
    const hh = String(newStart.getHours()).padStart(2, '0'); const mm = String(newStart.getMinutes()).padStart(2, '0'); const ss = String(newStart.getSeconds()).padStart(2, '0');
    setState(s => ({ ...s, startDate: `${y}-${m}-${d}`, startTime: `${hh}:${mm}:${ss}` }));
    setPaused(false); setPauseStartedMs(null); setShowZeroWhilePaused(false);
  }

  function handleReset() {
    const nowExact = new Date(); nowExact.setMilliseconds(0);
    if (paused) {
      const y = nowExact.getFullYear(); const m = String(nowExact.getMonth() + 1).padStart(2, '0'); const d = String(nowExact.getDate()).padStart(2, '0');
      const hh = String(nowExact.getHours()).padStart(2, '0'); const mm = String(nowExact.getMinutes()).padStart(2, '0'); const ss = String(nowExact.getSeconds()).padStart(2, '0');
      setState(s => ({ ...s, startDate: `${y}-${m}-${d}`, startTime: `${hh}:${mm}:${ss}` }));
      setPauseStartedMs(nowExact.getTime()); setPaused(true); setShowZeroWhilePaused(true); setZeroFlash(false);
      return;
    }
    setZeroFlash(true); setTimeout(() => setZeroFlash(false), ZERO_FLASH_MS + 16);
    setState(s => ({ ...s, startDate: currentLocalISODate(), startTime: nowHHMMSS() }));
    setPauseStartedMs(null); setShowZeroWhilePaused(false);
  }

  function buildGiftUrl(raw) { const n = Math.max(0.01, Number(raw) || 0); return `https://cash.app/$${CASH_TAG}/${n.toFixed(2)}`; }
  function handleGiftCopy() { const url = buildGiftUrl(giftAmount); try { navigator.clipboard.writeText(url); } catch {} }
  function handleGiftSend() { const url = buildGiftUrl(giftAmount); window.open(url, "_blank", "noopener,noreferrer"); }

  // static rates
  const perMinute = perSecond * 60;
  const perDay = perSecond * 86400;
  const perMonth = annualIncome / 12;
  const perYear = annualIncome;
  const perDayLabel = "Per day";
  const selectedDaysText = excludeNonWork ? compactDayRanges(workDays) : '';
  const scheduleText = excludeNonWork ? (`${selectedDaysText ? `${selectedDaysText} ` : ''}${workStart}–${workEnd}`) : '24/7';

  // Taxes (state + city)
  const [taxState, setTaxState] = useState('California');
  const [taxCity, setTaxCity] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const taxYear = 2025;
  const federalCfg = getFederalConfigForYear(taxYear);
  const stdDeduction = federalCfg.std[filingStatus] || federalCfg.std.single;
  const federalTaxable = Math.max(0, annualIncome - stdDeduction);
  const federalAnnualTax = computeProgressiveTax(federalTaxable, federalCfg.brackets[filingStatus] || federalCfg.brackets.single);
  // State
  const stateAnnualTax = computeStateTaxAnnual(annualIncome, taxState);
  // City
  const cityRatePrecise = 0;
  const cityAnnualTax = 0;
  // Total
  const annualTax = Math.max(0, federalAnnualTax + stateAnnualTax + cityAnnualTax);
  const perSecondTax = activeSecondsYearMemo > 0 ? (annualTax / activeSecondsYearMemo) : 0;
  const taxesPaid = perSecondTax * elapsedSec;
  const perMinuteTax = perSecondTax * 60;
  const perHourTax = perSecondTax * 3600;
  const perDayTax = perSecondTax * 86400;
  const perMonthTax = annualTax / 12;
  const perYearTax = annualTax;
  const effectiveTaxRate = annualIncome > 0 ? (annualTax / annualIncome) : 0;
  // After-tax (used in UI)
  const netAnnual = Math.max(0, annualIncome - annualTax);

  // Extras for big "Per minute" block
  const minuteElapsed = useMemo(() => {
    const ms = now.getMilliseconds ? now.getMilliseconds() : (nowMs % 1000);
    return (now.getSeconds() + (ms / 1000));
  }, [nowMs]);
  const minuteProgress = Math.max(0, Math.min(1, minuteElapsed / 60));
  const earnedThisMinuteSoFar = perSecond * (minuteElapsed);
  const perMinuteFixedStr = useMemo(() => sixFormatter.format(perMinute), [sixFormatter, perMinute]);
  const earnedFixedStr = useMemo(() => sixFormatter.format(earnedThisMinuteSoFar), [sixFormatter, earnedThisMinuteSoFar]);
  const earnedWidthCh = useMemo(() => perMinuteFixedStr.length, [perMinuteFixedStr]);

  const periodLabel = unit === 'hourly' ? 'Hourly' : (unit.charAt(0).toUpperCase() + unit.slice(1));
  const resetEnabled = reset !== 'never';
  const liveActive = !paused && perSecond > 0;

  // Savings & Purchases (local state)
  const [spMode, setSpMode] = useState('purchase');
  const [allocPurchasePct, setAllocPurchasePct] = useState(50);
  const [allocSavingsPct, setAllocSavingsPct] = useState(50);
  const allocPercent = spMode === 'purchase' ? allocPurchasePct : allocSavingsPct;
  const allocMultiplier = useMemo(() => {
    const pct = clamp(Number(allocPercent) || 0, 0, 100);
    return pct / 100;
  }, [allocPercent]);
  const [purchasePrice, setPurchasePrice] = useState("");
  const [savingsTarget, setSavingsTarget] = useState("");
  const purchaseSeconds = useMemo(() => {
    const price = Number(String(purchasePrice).replace(/,/g, '')) || 0;
    const eff = perSecond * Math.max(allocMultiplier, 0.000001);
    return eff > 0 ? price / eff : 0;
  }, [purchasePrice, perSecond, allocMultiplier]);
  const savingsSeconds = useMemo(() => {
    const target = Number(String(savingsTarget).replace(/,/g, '')) || 0;
    const eff = perSecond * Math.max(allocMultiplier, 0.000001);
    return eff > 0 ? target / eff : 0;
  }, [savingsTarget, perSecond, allocMultiplier]);
  const [purchaseFinishTs, setPurchaseFinishTs] = useState(null);
  const [savingsFinishTs, setSavingsFinishTs] = useState(null);
  useEffect(() => {
    if (purchaseSeconds > 0) {
      setPurchaseFinishTs(Date.now() + (purchaseSeconds * 1000));
    } else {
      setPurchaseFinishTs(null);
    }
  }, [purchaseSeconds]);
  useEffect(() => {
    if (savingsSeconds > 0) {
      setSavingsFinishTs(Date.now() + (savingsSeconds * 1000));
    } else {
      setSavingsFinishTs(null);
    }
  }, [savingsSeconds]);
  const effectiveDailyTowardGoal = useMemo(() => (perSecond * allocMultiplier * 86400), [perSecond, allocMultiplier]);
  const purchaseGoalAmount = useMemo(() => Number(String(purchasePrice).replace(/,/g, '')) || 0, [purchasePrice]);
  const savingsGoalAmount = useMemo(() => Number(String(savingsTarget).replace(/,/g, '')) || 0, [savingsTarget]);
  const [animPurchase, setAnimPurchase] = useState(0);
  const [animSavings, setAnimSavings] = useState(0);
  useEffect(() => {
    let raf = 0; const start = performance.now(); const from = 0; const to = purchaseSeconds;
    const dur = 380;
    const step = (t) => {
      const k = Math.min(1, (t - start) / dur);
      const ease = k < 0.5 ? 2*k*k : -1 + (4 - 2*k) * k; // easeInOutQuad
      setAnimPurchase(from + (to - from) * ease);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [purchaseSeconds]);
  useEffect(() => {
    let raf = 0; const start = performance.now(); const from = 0; const to = savingsSeconds;
    const dur = 380;
    const step = (t) => {
      const k = Math.min(1, (t - start) / dur);
      const ease = k < 0.5 ? 2*k*k : -1 + (4 - 2*k) * k;
      setAnimSavings(from + (to - from) * ease);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [savingsSeconds]);
  function formatDuration(totalSeconds) {
    const s = Math.max(0, Math.floor(totalSeconds || 0));
    const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); const m = Math.floor((s % 3600) / 60);
    const parts = [];
    if (d) parts.push(`${d}d`); if (h) parts.push(`${h}h`); if (m || (!d && !h)) parts.push(`${m}m`);
    return parts.join(' ');
  }

  // Famous people (seeded list + wikipedia thumbnails)
  const humanCacheRef = useRef(new Map());
  const famousThumbsRef = useRef(new Map());
  const [thumbsTick, setThumbsTick] = useState(0);
  const ALL_FAMOUS = [
    ['Jesus Christ', 0],
    ['Muhammad', 300e6],
    ['Buddha', 100e6],
    ['Confucius', 10e6],
    ['Gabriel Ramans (please hire me)', 0],
    ['Julius Caesar', 1e9],
    ['Alexander the Great', 500e6],
    ['Cleopatra', 300e6],
    ['Genghis Khan', 120e9],
    ['Kublai Khan', 110e9],
    ['Charlemagne', 3e9],
    ['William the Conqueror', 2e9],
    ['Leonardo da Vinci', 5e6],
    ['Michelangelo', 10e6],
    ['Galileo Galilei', 1e6],
    ['Isaac Newton', 20e6],
    ['Albert Einstein', 5e6],
    ['Nikola Tesla', 1e6],
    ['Thomas Edison', 15e6],
    ['Benjamin Franklin', 10e6],
    ['George Washington', 580e6],
    ['Thomas Jefferson', 100e6],
    ['Abraham Lincoln', 1e6],
    ['Napoleon Bonaparte', 250e6],
    ['Queen Victoria', 500e6],
    ['Winston Churchill', 10e6],
    ['Franklin D. Roosevelt', 20e6],
    ['John F. Kennedy', 30e6],
    ['Ronald Reagan', 20e6],
    ['Barack Obama', 15e6],
    ['Donald Trump', 450e6],
    ['Joe Biden', 1e6],
    ['Vladimir Putin', 4e9],
    ['Xi Jinping', 1e6],
    ['Mahatma Gandhi', 0],
    ['Mother Teresa', 0],
    ['Nelson Mandela', 1e6],
    ['Martin Luther King Jr.', 1e6],
    ['Malcolm X', 90e3],
    ['Karl Marx', 100e3],
    ['Friedrich Nietzsche', 100e3],
    ['Pablo Picasso', 50e6],
    ['Vincent van Gogh', 50e3],
    ['Andy Warhol', 20e6],
    ['Elvis Presley', 120e6],
    ['Michael Jackson', 200e6],
    ['Madonna', 125e6],
    ['Taylor Swift', 300e6],
    ['Beyoncé', 200e6],
    ['Rihanna', 150e6],
    ['Drake', 120e6],
    ['Eminem', 100e6],
    ['Kanye West', 250e6],
    ['Jay-Z', 300e6],
    ['Oprah Winfrey', 315e6],
    ['Ellen DeGeneres', 90e6],
    ['Dwayne Johnson', 120e6],
    ['Tom Cruise', 100e6],
    ['Robert Downey Jr.', 80e6],
    ['Leonardo DiCaprio', 80e6],
    ['Keanu Reeves', 50e6],
    ['Johnny Depp', 60e6],
    ['Will Smith', 80e6],
    ['Jackie Chan', 60e6],
    ['Bruce Lee', 10e6],
    ['Clint Eastwood', 50e6],
    ['Charlie Chaplin', 25e6],
    ['Marilyn Monroe', 15e6],
    ['Audrey Hepburn', 10e6],
    ['Elizabeth Taylor', 40e6],
    ['Kim Kardashian', 100e6],
    ['Kylie Jenner', 200e6],
    ['Kendall Jenner', 40e6],
    ['Gigi Hadid', 20e6],
    ['Cristiano Ronaldo', 275e6],
    ['Lionel Messi', 260e6],
    ['Neymar Jr.', 200e6],
    ['LeBron James', 130e6],
    ['Michael Jordan', 250e6],
    ['Kobe Bryant', 80e6],
    ['Shaquille O\'Neal', 60e6],
    ['Tiger Woods', 120e6],
    ['Serena Williams', 45e6],
    ['Roger Federer', 90e6],
    ['Usain Bolt', 20e6],
    ['Muhammad Ali', 30e6],
    ['Mike Tyson', 60e6],
    ['Conor McGregor', 200e6],
    ['Floyd Mayweather', 300e6],
    ['Phil Mickelson', 50e6],
    ['Jeff Bezos', 80e9],
    ['Elon Musk', 120e9],
    ['Bill Gates', 20e9],
    ['Steve Jobs', 2e9],
    ['Mark Zuckerberg', 15e9],
    ['Warren Buffett', 5e9],
    ['Larry Page', 8e9],
    ['Sergey Brin', 8e9],
    ['Bernard Arnault', 10e9],
    ['Andrew Carnegie', 14e9],
  ];
  const WIKI_TITLE_OVERRIDES = { 'drake': 'Drake (musician)' };
  const IMAGE_OVERRIDES = {
    'drake': 'https://media.licdn.com/dms/image/v2/D5603AQGWs97mrwRoyQ/profile-displayphoto-shrink_800_800/B56ZTQC3M7GsAc-/0/1738657200198?e=1758153600&v=beta&t=p7zKVRVdfQtAjfHaQkH3tLjj_gbVvK00TmeKq6Nx_TY',
    'gabriel-ramans-please-hire-me': 'https://pbs.twimg.com/profile_images/1933247272675880960/e6Sp8PoM_400x400.jpg'
  };
  const FAMOUS_LINK_OVERRIDES = { 'gabriel-ramans-please-hire-me': 'https://www.linkedin.com/in/gabrielramans/' };
  function makeFamous(name, annual) {
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return { id, name, img: '', annual, source: 'seed' };
  }
  const [famousList, setFamousList] = useState(() => ALL_FAMOUS.map(([n,a]) => makeFamous(n, a)));
  const [famousId, setFamousId] = useState('elon-musk');
  const famous = useMemo(() => famousList.find(p => p.id === famousId), [famousList, famousId]);
  const famousPerSecond = useMemo(() => (famous?.annual || 0) / secondsPer('yearly'), [famous]);
  const [famousStartMs, setFamousStartMs] = useState(() => Date.now());
  const [famousSync, setFamousSync] = useState(false);
  const [famousSearchQuery, setFamousSearchQuery] = useState("");
  const [famousSortMode, setFamousSortMode] = useState('none'); // 'none' | 'desc' | 'asc'
  // When person changes, reset start unless syncing to main timer
  useEffect(() => {
    if (famousSync) return; // keep in sync with main timer
    setFamousStartMs(Date.now());
  }, [famousId, famousSync]);
  // Sync start to main timer anchor when enabled
  useEffect(() => {
    if (!famousSync) return;
    const mainAnchor = combineDateTimeLocal(startDate, startTime).getTime();
    setFamousStartMs(mainAnchor);
  }, [famousSync, startDate, startTime]);
  const famousLive = useMemo(() => famousPerSecond * Math.max(0, (nowMs - famousStartMs) / 1000), [famousPerSecond, nowMs, famousStartMs]);
  const [hoverFamousId, setHoverFamousId] = useState(null);
  const leftProfileRef = useRef(null);
  const [leftPanelHeight, setLeftPanelHeight] = useState(null);
  useEffect(() => {
    function measure() {
      if (leftProfileRef.current) {
        const rect = leftProfileRef.current.getBoundingClientRect();
        setLeftPanelHeight(rect.height);
      }
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [dark, currency, famousId, thumbsTick]);

  function removeFamous(id) {
    setFamousList(curr => {
      const next = curr.filter(p => p.id !== id);
      if (id === famousId) {
        const nextId = next[0]?.id || '';
        setFamousId(nextId);
        setFamousStartMs(Date.now());
      }
      return next;
    });
  }

  // Preload thumbnails (from Wikipedia summaries) for any entries missing an image
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const batch = famousList;
      await Promise.all(batch.map(async (p) => {
        if (cancelled) return;
        if (p.img) return;
        // Apply hard image overrides (e.g., Drake)
        if (IMAGE_OVERRIDES[p.id]) {
          const overrideUrl = IMAGE_OVERRIDES[p.id];
          setFamousList(curr => curr.map(x => x.id === p.id ? { ...x, img: overrideUrl } : x));
          return;
        }
        try {
          const wikiTitle = WIKI_TITLE_OVERRIDES[p.id] || p.name;
          const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(wikiTitle)}`);
          if (!res.ok) return;
          const data = await res.json();
          const thumb = data?.thumbnail?.source || '';
          if (!cancelled && thumb) {
            setFamousList(curr => curr.map(x => x.id === p.id ? { ...x, img: thumb } : x));
          }
        } catch {}
      }));
    })();
    return () => { cancelled = true; };
  }, [famousList.length]);

  function addMoreFamous() {
    setFamousList(curr => {
      const existingIds = new Set(curr.map(p => p.id));
      const remaining = ALL_FAMOUS.filter(([n]) => !existingIds.has(n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')));
      const batch = remaining.slice(0, 8).map(([n,a]) => makeFamous(n, a));
      return batch.length ? [...curr, ...batch] : curr;
    });
  }

  const filteredFamousList = useMemo(() => {
    const q = (famousSearchQuery || '').toLowerCase();
    let arr = q ? famousList.filter(p => p.name.toLowerCase().includes(q)) : famousList.slice();
    if (famousSortMode === 'desc') {
      arr.sort((a, b) => {
        const da = (a.annual || 0); const db = (b.annual || 0);
        if (db !== da) return db - da;
        return a.name.localeCompare(b.name);
      });
    } else if (famousSortMode === 'asc') {
      arr.sort((a, b) => {
        const da = (a.annual || 0); const db = (b.annual || 0);
        if (da !== db) return da - db;
        const ga = a.id === 'gabriel-ramans-please-hire-me';
        const gb = b.id === 'gabriel-ramans-please-hire-me';
        if (ga && !gb) return -1;
        if (gb && !ga) return 1;
        return a.name.localeCompare(b.name);
      });
    }
    if (famousId) {
      const idx = arr.findIndex(p => p.id === famousId);
      if (idx > 0) {
        const [sel] = arr.splice(idx, 1);
        arr.unshift(sel);
      }
    }
    return arr;
  }, [famousList, famousSearchQuery, famousSortMode, famousId]);

  function addPersonByName(name) {
    const found = ALL_FAMOUS.find(([n]) => n === name);
    if (!found) return;
    const [n, a] = found;
    setFamousList(curr => {
      const id = n.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (curr.some(p => p.id === id)) return curr;
      const img = famousThumbsRef.current.get(id) || '';
      return [...curr, { ...makeFamous(n, a), img }];
    });
  }

  // Picker removed; fully static search over loaded list

  function normalizeName(s) { return String(s || '').trim(); }

  async function fetchWikipediaSuggestions(q) {
    if (!q) return [];
    const params = new URLSearchParams({
      action: 'query',
      generator: 'prefixsearch',
      gpssearch: q,
      gpsnamespace: '0',
      gpslimit: '6',
      prop: 'pageimages|pageprops',
      piprop: 'thumbnail',
      pithumbsize: '64',
      format: 'json',
      origin: '*',
      formatversion: '2',
    });
    const url = `https://en.wikipedia.org/w/api.php?${params.toString()}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    const pages = data?.query?.pages || [];
    return pages.map(p => ({
      pageid: p.pageid,
      title: p.title,
      thumb: p.thumbnail?.source || '',
      wikibase: p.pageprops?.wikibase_item || '',
    }));
  }

  async function isHumanWikidata(wikibaseId) {
    if (!wikibaseId) return false;
    if (humanCacheRef.current.has(wikibaseId)) return humanCacheRef.current.get(wikibaseId);
    try {
      const params = new URLSearchParams({ action: 'wbgetentities', ids: wikibaseId, format: 'json', origin: '*' });
      const url = `https://www.wikidata.org/w/api.php?${params.toString()}`;
      const res = await fetch(url);
      if (!res.ok) { humanCacheRef.current.set(wikibaseId, false); return false; }
      const data = await res.json();
      const claims = data?.entities?.[wikibaseId]?.claims || {};
      const p31 = claims['P31'] || [];
      const isHuman = p31.some(c => c?.mainsnak?.datavalue?.value?.id === 'Q5');
      humanCacheRef.current.set(wikibaseId, isHuman);
      return isHuman;
    } catch { humanCacheRef.current.set(wikibaseId, false); return false; }
  }

  // handleFamousQueryChange removed

  // addFamousFromSuggestion removed

  /* --------------------------------- UI -------------------------------- */
  return (
    <div style={S.page(dark)}>
      {/* Header */}
      <header style={{ ...S.header, borderBottomColor: dark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)" }}>
        <div style={{ ...S.container, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, paddingBottom: 16 }}>
        <div style={{ marginLeft: -1 }}>
          <div style={{ fontSize: 48, fontWeight: 800, letterSpacing: 1, textTransform: "none", color: dark ? "#fff" : "#000" }}>Pay Timer</div>
          <div style={{ ...S.headerMuted(dark), fontSize: 16, letterSpacing: 0.2, marginTop: 8 }}>What is your time worth?</div>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginRight: -1 }}>
          <div style={S.themeSegment(dark)}>
            <button
              type="button"
              aria-label="Light mode"
              onClick={() => setTheme("light")}
              style={S.themeOption(dark, !dark)}
              title="Light mode"
            >
              <SunIcon />
            </button>
            <button
              type="button"
              aria-label="Dark mode"
              onClick={() => setTheme("dark")}
              style={S.themeOption(dark, dark)}
              title="Dark mode"
            >
              <MoonIcon />
            </button>
          </div>
          <button onClick={() => setGiftOpen(true)}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(0,219,52,0.32), rgba(0,219,52,0.22))'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'linear-gradient(180deg, rgba(0,219,52,0.24), rgba(0,219,52,0.16))'; e.currentTarget.style.boxShadow = 'none'; }}
            style={{ ...S.presetBtn(dark, true), background: 'linear-gradient(180deg, rgba(0,219,52,0.24), rgba(0,219,52,0.16))', height: 40, padding: '0 14px' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <HeartIcon size={18} />
              <span style={{ ...S.presetLabel(dark), fontSize: 14 }}>Gift a Minute</span>
            </span>
          </button>
        </div>
        </div>
      </header>

      <main style={S.container}>
        <style>
          {`
          @keyframes paytimer-core {
            0% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 10px rgba(0,219,52,0.6); }
            50% { transform: translate(-50%, -50%) scale(1.15); box-shadow: 0 0 14px rgba(0,219,52,0.8); }
            100% { transform: translate(-50%, -50%) scale(1); box-shadow: 0 0 10px rgba(0,219,52,0.6); }
          }
          @keyframes paytimer-wave {
            0% { transform: translate(-50%, -50%) scale(0.4); opacity: 0.5; }
            70% { opacity: 0; }
            100% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
          }
          @keyframes donutSpin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes captionFadeUp {
            0% { opacity: 0; transform: translateY(6px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          `}
        </style>
        {/* 2-col grid on wide screens */}
        <div style={{ ...S.grid, ...(window.innerWidth >= 900 ? S.grid2 : {}) }}>
          {/* Clock */}
          <section style={{ ...S.heroCard(dark), gridColumn: window.innerWidth >= 900 ? "1 / span 2" : "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, ...S.muted(dark) }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={S.pulseWrap}>
                  {liveActive ? (
                    <>
                      <span style={S.pulseWave()} />
                      <span style={S.pulseWave('0.9s')} />
                      <span style={S.pulseCore} />
                    </>
                  ) : (
                    <span style={S.pulseDisabled(dark)} />
                  )}
                </span>
                <div style={{ textTransform: "uppercase", letterSpacing: 0.6, fontSize: 12 }}>Live Pay Timer</div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" aria-label={paused ? 'Resume' : 'Pause'} onClick={handlePauseToggle} style={S.iconBtn(dark)} title={paused ? 'Resume' : 'Pause'}>
                  {paused ? <PlayIcon /> : <PauseIcon />}
                </button>
                <button type="button" aria-label="Reset" onClick={handleReset} style={S.iconBtn(dark)} title="Reset">
                  <ResetIcon />
                </button>
              </div>
            </div>
            <div style={{ ...S.monoBig, opacity: paused ? 0.9 : 1, fontSize: (typeof window !== 'undefined' && window.innerWidth < 600) ? 36 : 64 }}>
              {(zeroFlash || (paused && showZeroWhilePaused) ? sixFormatter : formatter).format((zeroFlash || (paused && showZeroWhilePaused)) ? 0 : liveValue)}
            </div>
            <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              <button
                type="button"
                onClick={() => taxesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                onMouseEnter={() => setTaxBadgeHover(true)}
                onMouseLeave={() => setTaxBadgeHover(false)}
                style={{
                  ...S.badge('rgba(255,59,48,0.85)'),
                  background: taxBadgeHover ? 'rgba(255,59,48,0.12)' : 'rgba(255,59,48,0.06)',
                  borderColor: taxBadgeHover ? 'rgba(255,59,48,0.28)' : 'rgba(255,59,48,0.18)',
                  boxShadow: taxBadgeHover ? '0 1px 4px rgba(0,0,0,0.25)' : 'none',
                  transition: 'background 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
                  cursor: 'pointer',
                  fontSize: 13
                }}
              >Tax</button>
              <span style={{ color: '#ff3b30', fontFamily: S.monoBig.fontFamily, fontWeight: 700, fontSize: 16, fontVariantNumeric: 'tabular-nums', display: 'inline-block', marginLeft: 6 }}>
                {sixFormatter.format(Math.max(0, taxesPaid))}
              </span>
              <span style={{ ...S.badge('rgba(0,219,52,0.9)'), background: 'rgba(0,219,52,0.06)', borderColor: 'rgba(0,219,52,0.18)', marginLeft: 8, fontSize: 13 }}>After tax</span>
              <span style={{ color: '#00db34', fontFamily: S.monoBig.fontFamily, fontWeight: 700, fontSize: 16, fontVariantNumeric: 'tabular-nums', display: 'inline-block', marginLeft: 6 }}>
                {sixFormatter.format(Math.max(0, (zeroFlash || (paused && showZeroWhilePaused)) ? 0 : (liveValue - taxesPaid)))}
              </span>
            </div>
            <div style={{ marginTop: 10, ...S.muted(dark) }}>
              Rate: {formatMoney(perSecond, currency)} per second {excludeNonWork ? "(work hours only)" : "(24/7)"} {paused ? "• paused" : ""}
            </div>
          </section>

          


          {/* Income Details */}
          <section style={{ ...S.card(dark), minWidth: 0 }}>
            <div style={{ ...S.muted(dark), textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Income Details</div>

            {unit !== "hourly" ? (
              <div style={window.innerWidth >= 900 ? S.row3 : S.row}>
                <div style={{ gridColumn: window.innerWidth >= 900 ? "span 2" : "auto", display: "flex", flexDirection: "column" }}>
                  <FieldLabel dark={dark}>{periodLabel} Income</FieldLabel>
                  <PrefixedInput
                    dark={dark}
                    id="amount" prefix={currencySymbol} value={displayAmount} onChange={handleAmount}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FieldLabel dark={dark}>Pay period</FieldLabel>
                  <select value={unit} onChange={(e) => setState(s => ({ ...s, unit: e.target.value }))} style={S.select(dark)}>
                    <option value="hourly">hourly</option>
                    <option value="daily">daily</option>
                    <option value="monthly">monthly</option>
                    <option value="yearly">yearly</option>
                  </select>
                </div>
              </div>
            ) : (
              <div style={window.innerWidth >= 900 ? S.row3 : S.row}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FieldLabel dark={dark}>Hourly rate</FieldLabel>
                  <PrefixedInput
                    dark={dark}
                    id="hourlyRate" prefix={currencySymbol} value={displayHourlyRate} onChange={handleHourlyRate}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FieldLabel dark={dark}>Hours per week</FieldLabel>
                  <ZeroInput dark={dark} id="hoursPerWeek" value={hoursPerWeek} onChange={handleHoursPerWeek} />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FieldLabel dark={dark}>Pay period</FieldLabel>
                  <select value={unit} onChange={(e) => setState(s => ({ ...s, unit: e.target.value }))} style={S.select(dark)}>
                    <option value="hourly">hourly</option>
                    <option value="daily">daily</option>
                    <option value="monthly">monthly</option>
                    <option value="yearly">yearly</option>
                  </select>
                </div>
              </div>
            )}

            {/* Quick start presets (single-select circular checkboxes) */}
            <div style={{ marginTop: 12 }}>
              <FieldLabel dark={dark}>Timer Start</FieldLabel>
            </div>
            <div style={window.innerWidth >= 900
              ? { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 8 }
              : { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <button
                type="button"
                onClick={() => {
                  setStartPreset('now');
                  const now = new Date();
                  const y = now.getFullYear();
                  const m = String(now.getMonth() + 1).padStart(2, '0');
                  const d = String(now.getDate()).padStart(2, '0');
                  const hh = String(now.getHours()).padStart(2, '0');
                  const mm = String(now.getMinutes()).padStart(2, '0');
                  const ss = String(now.getSeconds()).padStart(2, '0');
                  setState(s => ({ ...s, startDate: `${y}-${m}-${d}`, startTime: `${hh}:${mm}:${ss}` }));
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = startPreset==='now' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = startPreset==='now' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={{ ...S.presetBtn(dark, startPreset === 'now'), ...(window.innerWidth < 900 ? { minWidth: 0 } : {}), minWidth: 'max-content' }}
                title="Right now"
              >
                <span style={S.presetCircle(dark, startPreset === 'now')} />
                <span style={{ ...S.presetLabel(dark), overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap', flex: '0 0 auto' }}>Now</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartPreset('today');
                  const now = new Date();
                  const y = now.getFullYear();
                  const m = String(now.getMonth() + 1).padStart(2, '0');
                  const d = String(now.getDate()).padStart(2, '0');
                  setState(s => ({ ...s, startDate: `${y}-${m}-${d}`, startTime: '00:00:00' }));
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = startPreset==='today' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = startPreset==='today' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={{ ...S.presetBtn(dark, startPreset === 'today'), ...(window.innerWidth < 900 ? { minWidth: 0 } : {}), minWidth: 'max-content' }}
                title="Today, 00:00"
              >
                <span style={S.presetCircle(dark, startPreset === 'today')} />
                <span style={{ ...S.presetLabel(dark), overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap', flex: '0 0 auto' }}>Today</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartPreset('firstOfMonth');
                  const now = new Date();
                  const y = now.getFullYear();
                  const m = String(now.getMonth() + 1).padStart(2, '0');
                  setState(s => ({ ...s, startDate: `${y}-${m}-01`, startTime: '00:00:00' }));
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = startPreset==='firstOfMonth' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = startPreset==='firstOfMonth' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={{ ...S.presetBtn(dark, startPreset === 'firstOfMonth'), ...(window.innerWidth < 900 ? { minWidth: 0 } : {}), minWidth: 'max-content' }}
                title="First of this month, 00:00"
              >
                <span style={S.presetCircle(dark, startPreset === 'firstOfMonth')} />
                <span style={{ ...S.presetLabel(dark), overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap', flex: '0 0 auto' }}>This Month</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setStartPreset('jan1');
                  const now = new Date();
                  const y = now.getFullYear();
                  setState(s => ({ ...s, startDate: `${y}-01-01`, startTime: '00:00:00' }));
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = startPreset==='jan1' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = startPreset==='jan1' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={{ ...S.presetBtn(dark, startPreset === 'jan1'), ...(window.innerWidth < 900 ? { minWidth: 0 } : {}), minWidth: 'max-content' }}
                title="First of this year, 00:00"
              >
                <span style={S.presetCircle(dark, startPreset === 'jan1')} />
                <span style={{ ...S.presetLabel(dark), overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap', flex: '0 0 auto' }}>This Year</span>
              </button>
              <button
                type="button"
                onClick={() => setStartPreset('custom')}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = startPreset==='custom' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = startPreset==='custom' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={{ ...S.presetBtn(dark, startPreset === 'custom'), ...(window.innerWidth < 900 ? { minWidth: 0 } : {}), minWidth: 'max-content' }}
                title="Use custom date and time"
              >
                <span style={S.presetCircle(dark, startPreset === 'custom')} />
                <span style={{ ...S.presetLabel(dark), overflow: 'visible', textOverflow: 'clip', whiteSpace: 'nowrap', flex: '0 0 auto' }}>Custom</span>
              </button>
            </div>

            {/* Start date/time/currency (shows when Custom is active) */}
            <div style={{ height: startPreset === 'custom' ? 'auto' : 0, transition: 'height 160ms ease', overflow: 'hidden', marginTop: 16 }}>
            {startPreset === 'custom' && (
            <div ref={startFieldsRef} key={`start-fields-${unit}`} style={{ ...(window.innerWidth >= 900 ? S.row3 : S.row) }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel dark={dark}>Start date</FieldLabel>
                  <div style={{ position: 'relative' }}>
                    <button type="button" onClick={() => openNativePicker(dateInputRef)} style={S.inputLeftIconBtn(dark)} title="Open date picker">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                    </button>
                    <input className="no-native-icon" ref={dateInputRef} type="date" value={startDate} max={TODAY_MAX}
                  onChange={(e) => setState(s => ({ ...s, startDate: e.target.value }))}
                      style={{ ...S.input(dark), paddingLeft: 36, paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      aria-label="Reset to first of month"
                      title="Reset to first of month"
                      onClick={() => {
                        const d = new Date();
                        const y = d.getFullYear();
                        const m = String(d.getMonth() + 1).padStart(2, '0');
                        const day = '01';
                        setState(s => ({ ...s, startDate: `${y}-${m}-${day}` }));
                      }}
                      style={S.inputOverlayBtn(dark)}
                    >
                      <ResetIcon size={16} />
                    </button>
              </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel dark={dark}>Start time</FieldLabel>
                  <div style={{ position: 'relative' }}>
                    <button type="button" onClick={() => openNativePicker(timeInputRef)} style={S.inputLeftIconBtn(dark)} title="Open time picker">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                    </button>
                    <input className="no-native-icon" ref={timeInputRef} type="time" step={1} value={startTime}
                  onChange={(e) => setState(s => ({ ...s, startTime: e.target.value.length === 5 ? e.target.value + ":00" : e.target.value }))}
                      style={{ ...S.input(dark), paddingLeft: 36, paddingRight: 44 }}
                    />
                    <button
                      type="button"
                      aria-label="Reset to 12:00 AM"
                      title="Reset to 12:00 AM"
                      onClick={() => setState(s => ({ ...s, startTime: '00:00:00' }))}
                      style={S.inputOverlayBtn(dark)}
                    >
                      <ResetIcon size={16} />
                    </button>
              </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel dark={dark}>Currency</FieldLabel>
                <select value={currency} onChange={(e) => setState(s => ({ ...s, currency: e.target.value }))} style={S.select(dark)}>
                  {["USD","EUR","GBP","CAD","AUD","JPY","INR","BRL","MXN","SEK","CHF","CNY"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            )}
            </div>
          </section>

          

          {/* What you're making */}
          <section style={S.card(dark)}>
            <div style={{ ...S.muted(dark), textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>What you're making</div>
            <div style={window.innerWidth >= 900 ? { display: 'grid', gap: 16, gridTemplateColumns: '2fr 1fr 1fr' } : S.row}>
              {/* Big Per minute block spanning two rows on wide screens, anchored left */}
              <div style={{
                ...S.statBox(dark),
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: window.innerWidth >= 900 ? 104 : undefined,
                gridRow: window.innerWidth >= 900 ? 'span 2' : 'auto',
                gridColumn: window.innerWidth >= 900 ? '1 / 2' : 'auto',
                minWidth: 0
              }}>
                <button
                  type="button"
                  aria-label="Copy per-minute"
                  onClick={() => { try { navigator.clipboard.writeText(perMinute.toFixed(2)); } catch {} }}
                  style={S.copyBtn(dark)}
                  title="Copy"
                >
                  ⧉
                </button>
                <div style={{ ...S.muted(dark), textTransform: 'uppercase', letterSpacing: 0.6 }}>Per minute</div>
                <div style={{ ...S.bigDuration(dark), lineHeight: 1, marginTop: 2, fontVariantNumeric: 'tabular-nums', display: 'inline-block' }}>
                  {formatMoney(perMinute, currency)}
                </div>
              <div style={{ marginTop: 6, display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <div style={{ position: 'relative', height: 6, background: dark ? '#1a1a1a' : '#eee', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '100%', background: '#00db34', transform: `scaleX(${minuteProgress})`, transformOrigin: 'left center', willChange: 'transform' }} />
                  </div>
                  <div style={{ ...S.muted(dark), fontSize: 12, fontFamily: S.monoBig.fontFamily, fontVariantNumeric: 'tabular-nums', width: '3ch', textAlign: 'right' }}>{Math.floor(minuteElapsed).toString().padStart(2, '0')}s</div>
                </div>
                <div style={{ marginTop: 6, ...S.muted(dark), display: 'flex', justifyContent: 'space-between' }}>
                  <span>Earned so far</span>
                  <span style={{ fontFamily: S.monoBig.fontFamily, display: 'inline-block', fontVariantNumeric: 'tabular-nums', width: `${earnedWidthCh}ch`, textAlign: 'right' }}>{earnedFixedStr}</span>
                </div>
              </div>

              <Stat dark={dark} label="Per second" value={formatMoney(perSecond, currency)} copyText={perSecond.toFixed(6)} />
              <Stat dark={dark} label={perDayLabel} value={formatMoney(perDay, currency)} copyText={perDay.toFixed(2)} />
              <Stat dark={dark} label="Per month" value={formatMoney(perMonth, currency)} copyText={perMonth.toFixed(2)} />
              <Stat dark={dark} label="Per year" value={formatMoney(perYear, currency)} copyText={perYear.toFixed(2)} />
            </div>
          </section>

{/* Savings & Purchases (toggle) */}
<section style={S.card(dark)}>
            <div style={{ ...S.muted(dark), textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>Savings & Purchases</div>
            <FieldLabel dark={dark}>Convert a price or savings goal into time.</FieldLabel>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button type="button" onClick={() => setSpMode('purchase')}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = spMode==='purchase' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = spMode==='purchase' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={S.presetBtn(dark, spMode === 'purchase')}>
                <span style={S.presetCircle(dark, spMode === 'purchase')} />
                <span style={S.presetLabel(dark)}>Purchases</span>
              </button>
              <button type="button" onClick={() => setSpMode('savings')}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.25)'; e.currentTarget.style.borderColor = spMode==='savings' ? '#00db34' : (dark ? '#444' : '#aaa'); }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = spMode==='savings' ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)'); }}
                style={S.presetBtn(dark, spMode === 'savings')}>
                <span style={S.presetCircle(dark, spMode === 'savings')} />
                <span style={S.presetLabel(dark)}>Savings</span>
              </button>
            </div>

            {spMode === 'purchase' ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>Item price</FieldLabel>
                    <PrefixedInput
                      dark={dark}
                      id="purchasePrice"
                      prefix={currencySymbol}
                      value={formatNumericWithCommas(purchasePrice)}
                      onChange={(e) => setPurchasePrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>What percentage of your income do you want to allocate</FieldLabel>
                    <AllocSlider dark={dark} value={allocPurchasePct} onChange={(v) => setAllocPurchasePct(v)} inputId="allocPercentPurchase" />
                  </div>
                </div>
              <div>
                  <div style={{ marginTop: 4 }}>
                    <FieldLabel dark={dark}>Time to buy</FieldLabel>
                  </div>
                  <div style={{ ...S.displayField(dark, !(perSecond > 0)), height: 56, fontSize: 20 }}>
                    <span style={S.bigDuration(dark)}>{perSecond > 0 ? formatDuration(animPurchase) : ''}</span>
                  </div>
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: (window.innerWidth >= 900 ? '1fr 1fr' : '1fr'), gap: 8 }}>
                    <Stat dark={dark} label="Finish date" value={purchaseSeconds > 0 ? new Date(purchaseFinishTs).toLocaleString() : '—'} />
                    <Stat dark={dark} label="Daily toward goal" value={formatMoney(Math.min(effectiveDailyTowardGoal, purchaseGoalAmount), currency)} />
                  </div>
                  
                </div>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>Savings target</FieldLabel>
                    <PrefixedInput
                      dark={dark}
                      id="savingsTarget"
                      prefix={currencySymbol}
                      value={formatNumericWithCommas(savingsTarget)}
                      onChange={(e) => setSavingsTarget(e.target.value.replace(/[^0-9.]/g, ""))}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>What percentage of your income do you want to allocate</FieldLabel>
                    <AllocSlider dark={dark} value={allocSavingsPct} onChange={(v) => setAllocSavingsPct(v)} inputId="allocPercentSavings" />
                  </div>
              </div>
              <div>
                  <div style={{ marginTop: 4 }}>
                    <FieldLabel dark={dark}>Time to save</FieldLabel>
                  </div>
                  <div style={{ ...S.displayField(dark, !(perSecond > 0)), height: 56, fontSize: 20 }}>
                    <span style={S.bigDuration(dark)}>{perSecond > 0 ? formatDuration(animSavings) : ''}</span>
                  </div>
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: (window.innerWidth >= 900 ? '1fr 1fr' : '1fr'), gap: 8 }}>
                    <Stat dark={dark} label="Finish date" value={savingsSeconds > 0 ? new Date(savingsFinishTs).toLocaleString() : '—'} />
                    <Stat dark={dark} label="Daily toward goal" value={formatMoney(Math.min(effectiveDailyTowardGoal, savingsGoalAmount), currency)} />
                  </div>
                  
                </div>
              </div>
            )}
          </section>
                  
          {/* Famous People */}
          <section style={S.card(dark)}>
            <div style={{ ...S.muted(dark), textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>Famous people</div>

            {/* Two-column layout on desktop; single column on mobile with compact preview */}
            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: (window.innerWidth >= 900 ? '200px 1fr' : '1fr'), alignItems: 'start', minWidth: 0 }}>
              {/* Left profile */}
              <div style={S.statBox(dark)} ref={leftProfileRef}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, minWidth: 0 }}>
                  <span style={{ width: (typeof window !== 'undefined' && window.innerWidth < 600) ? 80 : '100%', height: (typeof window !== 'undefined' && window.innerWidth < 600) ? 80 : 'auto', aspectRatio: '1 / 1', display: 'block', borderRadius: 2, overflow: 'hidden', background: dark ? '#222' : '#eee' }}>
                    {famous?.img ? <img src={famous.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} /> : null}
                  </span>
                  <div style={{ fontWeight: 700 }}>
                    {famous?.id === 'gabriel-ramans-please-hire-me' ? (
                      <>
                        <a href={FAMOUS_LINK_OVERRIDES['gabriel-ramans-please-hire-me']} target="_blank" rel="noopener noreferrer" style={{ color: dark ? '#fff' : '#000', textDecoration: 'underline' }}>Gabriel Ramans</a>
                        <span> (please hire me)</span>
                      </>
                    ) : (
                      famous?.name || '—'
                    )}
                  </div>
                  <div style={{ ...S.muted(dark), marginTop: 2 }}>Yearly: {formatCompactCurrency(famous?.annual || 0, currency)}</div>
                  <div style={{ ...S.muted(dark), marginTop: 2 }}>Per second: {formatMoney(famousPerSecond, currency)}</div>
                </div>
              </div>

              {/* Right options */}
              <div style={{ minWidth: 0 }}>
                <div style={{
                  border: `1px solid ${dark ? '#292929' : 'rgba(0,0,0,0.2)'}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  background: dark ? '#0a0a0a' : '#fff',
                  height: (window.innerWidth >= 900 && leftPanelHeight) ? leftPanelHeight : 'auto',
                  maxHeight: window.innerWidth >= 900 ? (leftPanelHeight || 320) : 'none',
                  position: 'relative'
                }}>
                  <div style={{ position: 'sticky', top: 0, zIndex: 1, padding: 8, background: dark ? '#0a0a0a' : '#fff', borderBottom: `1px solid ${dark ? '#292929' : 'rgba(0,0,0,0.2)'}`, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      value={famousSearchQuery}
                      onChange={(e) => setFamousSearchQuery(e.target.value)}
                      placeholder="Search people…"
                      style={{ ...S.input(dark), height: 32, padding: '6px 10px', flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setFamousSortMode(m => (m === 'none' ? 'desc' : m === 'desc' ? 'asc' : 'none'))}
                      title={famousSortMode === 'desc' ? 'Sort: High to low' : famousSortMode === 'asc' ? 'Sort: Low to high' : 'Sort: Off'}
                      style={{ ...S.outlineBtn(dark), height: 32, width: 36, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {famousSortMode === 'desc' ? <ArrowUpIcon /> : famousSortMode === 'asc' ? <ArrowDownIcon /> : <SortIcon />}
                    </button>
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: '100%' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: (typeof window !== 'undefined' && window.innerWidth < 640) ? 'repeat(1, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: 8, padding: 8 }}>
                      {filteredFamousList.slice(0, (typeof window !== 'undefined' && window.innerWidth < 640) ? 6 : filteredFamousList.length).map(p => (
                        <div
                          key={p.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setFamousId(curr => (curr === p.id ? '' : p.id))}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFamousId(p.id); } }}
                          style={{ ...S.presetBtn(dark, p.id === famousId), justifyContent: 'flex-start', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          <span style={{ width: 24, height: 24, display: 'inline-block', borderRadius: 2, overflow: 'hidden', background: dark ? '#222' : '#eee' }}>
                            {p.img ? <img src={p.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} /> : null}
                          </span>
                          <span style={{ ...S.presetLabel(dark), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                          <span style={{ ...S.muted(dark), marginLeft: 'auto', fontSize: 12 }}>{formatCompactCurrency(p.annual, currency)}</span>
                        </div>
                      ))}
                    </div>
                    {typeof window !== 'undefined' && window.innerWidth < 640 && filteredFamousList.length > 6 && (
                      <div style={{ padding: 8, display: 'flex', justifyContent: 'center' }}>
                        <button type="button" onClick={addMoreFamous} style={{ ...S.outlineBtn(dark) }}>See all</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

              {/* Bottom timer + sync toggle */}
            <div style={{ marginTop: 12, borderTop: `1px solid ${dark ? '#292929' : 'rgba(0,0,0,0.2)'}`, paddingTop: 12 }}>
              <div style={{ ...S.muted(dark), marginBottom: 6 }}>Live value if you earned like {famous?.name || '—'}</div>
              <div style={{ ...S.bigDuration(dark), lineHeight: 1 }}>
                {formatter.format(famousLive)}
                  </div>
              <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={famousSync}
                    onClick={() => setFamousSync(v => !v)}
                    style={{ ...S.switchTrack(dark, famousSync) }}
                    title={famousSync ? 'on' : 'off'}
                  >
                    <span style={S.switchThumb(dark, famousSync)} />
                  </button>
                  <span style={{ ...S.muted(dark), fontSize: 12, opacity: 0.75 }}>Sync with your timer</span>
                </div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, ...S.muted(dark), fontSize: 11, opacity: 0.55 }}>
                  <GrokIcon size={12} />
                  <span>
                    Yearly incomes estimated by{' '}
                    <a href="https://grok.com" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>Grok</a>
                  </span>
              </div>
            </div>
            </div>
          </section>


          {/* Timer Settings */}
          <section style={S.card(dark)}>
            <div style={{ ...S.muted(dark), textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Timer Settings</div>

            <div style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: (window.innerWidth >= 900)
                ? (resetEnabled
                    ? (reset === 'yearly' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr')
                    : '1fr')
                : '1fr'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <FieldLabel dark={dark}>Auto-reset</FieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={resetEnabled}
                    onClick={() => setState(s => ({ ...s, reset: (s.reset === 'never') ? 'daily' : 'never' }))}
                    style={S.switchTrack(dark, resetEnabled)}
                    title={resetEnabled ? 'on' : 'off'}
                  >
                    <span style={S.switchThumb(dark, resetEnabled)} />
                  </button>
                  <span style={S.switchText(dark, resetEnabled)}>{resetEnabled ? 'on' : 'off'}</span>
                  </div>
              </div>
              {resetEnabled && (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>Period</FieldLabel>
                    <select value={reset} onChange={(e) => setState(s => ({ ...s, reset: e.target.value }))} style={S.select(dark)}>
                      <option value="daily">daily</option>
                      <option value="weekly">weekly</option>
                      <option value="monthly">monthly</option>
                      <option value="yearly">yearly</option>
                    </select>
                  </div>
                  {reset === 'daily' && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FieldLabel dark={dark}>Time</FieldLabel>
                      <input type="time" step={60} value={dailyResetTime} onChange={(e)=> setState(s=> ({...s, dailyResetTime: e.target.value }))} style={S.input(dark)} />
                </div>
              )}
                  {reset === 'weekly' && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FieldLabel dark={dark}>Day</FieldLabel>
                      <select value={String(weeklyResetDOW)} onChange={(e)=> setState(s=> ({...s, weeklyResetDOW: Number(e.target.value)}))} style={S.select(dark)}>
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                </div>
              )}
                  {reset === 'monthly' && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FieldLabel dark={dark}>Day</FieldLabel>
                      <input type="number" min={1} max={31} value={monthlyResetDOM} onChange={(e)=> setState(s=> ({...s, monthlyResetDOM: clamp(Number(e.target.value) || 1, 1, 31)}))} style={S.input(dark)} />
                </div>
              )}
                  {reset === 'yearly' && (
                    <>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>Month</FieldLabel>
                        <select value={String(yearlyResetMonth)} onChange={(e)=> setState(s=> ({...s, yearlyResetMonth: Number(e.target.value)}))} style={S.select(dark)}>
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, idx)=> (
                            <option key={m} value={String(idx+1)}>{m}</option>
                      ))}
                    </select>
                  </div>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <FieldLabel dark={dark}>Day</FieldLabel>
                        <input type="number" min={1} max={31} value={yearlyResetDay} onChange={(e)=> setState(s=> ({...s, yearlyResetDay: clamp(Number(e.target.value) || 1, 1, 31)}))} style={S.input(dark)} />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div style={{ borderTop: `1px solid ${dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`, marginTop: 16, paddingTop: 16 }}>
              <div style={S.row2}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <FieldLabel dark={dark}>Exclude non working hours</FieldLabel>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={excludeNonWork}
                      onClick={() => setState(s => {
                        const nextExclude = !s.excludeNonWork;
                        const nextIncludeWeekends = nextExclude ? false : s.includeWeekends;
                        return { ...s, excludeNonWork: nextExclude, includeWeekends: nextIncludeWeekends };
                      })}
                      style={S.switchTrack(dark, excludeNonWork)}
                      title={excludeNonWork ? 'on' : 'off'}
                    >
                      <span style={S.switchThumb(dark, excludeNonWork)} />
                    </button>
                    <span style={S.switchText(dark, excludeNonWork)}>{excludeNonWork ? 'on' : 'off'}</span>
                  </div>
                </div>
              </div>

              {excludeNonWork && (
                <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
                  <div style={{ ...S.muted(dark) }}>Working days</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {[ 'Sun','Mon','Tue','Wed','Thu','Fri','Sat' ].map((dLabel, idx) => (
                      <button key={dLabel} type="button" onClick={() => setState(s => { const next = [...s.workDays]; next[idx] = !next[idx]; return { ...s, workDays: next }; })} style={{
                        border: `1px solid ${workDays[idx] ? '#00db34' : (dark ? '#292929' : 'rgba(0,0,0,0.25)')}`,
                        background: 'transparent', color: dark ? '#fff' : '#000',
                        padding: '4px 8px', borderRadius: 2, cursor: 'pointer', fontSize: 12
                      }}>{dLabel}</button>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: window.innerWidth >= 900 ? '1fr 1fr' : '1fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FieldLabel dark={dark}>Work start</FieldLabel>
                      <input type="time" step={60} value={workStart} onChange={(e) => setState(s => ({ ...s, workStart: e.target.value }))} style={S.input(dark)} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <FieldLabel dark={dark}>Work end</FieldLabel>
                      <input type="time" step={60} value={workEnd} onChange={(e) => setState(s => ({ ...s, workEnd: e.target.value }))} style={S.input(dark)} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Conditional schedule controls removed (now inline with Period) */}
          </section>

          {/* Timer Details */}
          <section style={S.card(dark)}>
            <div style={{ ...S.muted(dark), textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Timer Details</div>
            <div style={window.innerWidth >= 900 ? { ...S.row3 } : S.row2}>
              <Stat dark={dark} label="Since" value={new Date(anchor).toLocaleString()} />
              <Stat dark={dark} label="Start date" value={new Date(combineDateTimeLocal(startDate, "00:00:00")).toLocaleDateString()} />
              <Stat dark={dark} label="Start time" value={startTime} />
              <Stat dark={dark} label="Next reset" value={reset === 'never' ? 'never' : (scheduledResetTs ? new Date(scheduledResetTs).toLocaleString() : '—')} />
              <Stat dark={dark} label="Pay period" value={unit} />
              <Stat dark={dark} label="Schedule" value={scheduleText} />
            </div>
          </section>

          {/* Taxes (wide) */}
          <section ref={taxesRef} style={{ ...S.card(dark), gridColumn: window.innerWidth >= 900 ? '1 / span 2' : 'auto' }}>
            <div style={{ ...S.muted(dark), textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>Taxes</div>
            <div style={{ marginTop: 12, display: 'grid', gap: 12, gridTemplateColumns: window.innerWidth >= 900 ? '1fr 1fr' : '1fr' }}>
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, alignItems: 'end', width: '100%', marginBottom: 12 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                     <FieldLabel dark={dark}>State</FieldLabel>
                    <select value={taxState} onChange={(e)=> setTaxState(e.target.value)} style={{ ...S.select(dark), width: '100%' }}>
                      {US_STATES.map(st => <option key={st.code} value={st.name}>{st.name}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                     <FieldLabel dark={dark}>Filing status</FieldLabel>
                    <select value={filingStatus} onChange={(e)=> setFilingStatus(e.target.value)} style={{ ...S.select(dark), width: '100%' }}>
                      <option value="single">single</option>
                      <option value="married">married filing jointly</option>
                      <option value="hoh">head of household</option>
                    </select>
                  </div>
                </div>
                <div style={{ ...S.statBox(dark) }}>
                   <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                     <div style={{ ...S.muted(dark), textTransform: 'uppercase', letterSpacing: 0.6 }}>Taxes paid so far</div>
                     <div style={{ ...S.muted(dark), whiteSpace: 'nowrap' }}>Effective rate: {(effectiveTaxRate*100).toFixed(2)}% • {taxState}</div>
                   </div>
                   <div style={{ marginTop: 12 }}>
                     <div>
                       <div style={{ ...S.bigDuration(dark), lineHeight: 1.05 }}>{formatter.format(taxesPaid)}</div>
                       {(() => {
                         const frac = Math.max(0, Math.min(1, effectiveTaxRate || 0));
                         const minutesExact = frac * (currentPeriod?.minutes || 60);
                         const sentenceKey = `cap-${periodIdx}-${minutesExact.toFixed(2)}`;
                         return (
                           <>
                             <div style={{ margin: '10px 0', height: 1, background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} />
                             <div key={sentenceKey} style={{ ...S.muted(dark), margin: '8px 0', lineHeight: 1.3, fontSize: 16, textAlign: 'left', animation: 'captionFadeUp 240ms ease' }}>
                               <span>For every </span>
                               <span style={{ fontWeight: 800, fontSize: 18, color: dark ? '#fff' : '#000', display: 'inline-block' }}>{currentPeriod?.label || '1 hour'}</span>
                               <span> you work, </span>
                               <span style={{ fontWeight: 900, fontSize: 18, color: '#ff3b30', display: 'inline-block' }}>{formatLargestDecimalFromMinutes(minutesExact)}</span>
                               <span> go to the government.</span>
                             </div>
                           </>
                         );
                       })()}
                     </div>
                   </div>
                   <div style={{ margin: '2px 0 4px', height: 1, background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} />
                 </div>
              </div>
              <div style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginTop: 0 }}>
                <Stat compact dark={dark} label="Per second" value={formatMoney(perSecondTax, currency)} />
                <Stat compact dark={dark} label="Per minute" value={formatMoney(perMinuteTax, currency)} />
                <Stat compact dark={dark} label="Per hour" value={formatMoney(perHourTax, currency)} />
                <Stat compact dark={dark} label="Per day" value={formatMoney(perDayTax, currency)} />
                <Stat compact dark={dark} label="Per month" value={formatMoney(perMonthTax, currency)} />
                <Stat compact dark={dark} label="Per year" value={formatMoney(perYearTax, currency)} />
                <Stat compact dark={dark} label="Federal/year" value={formatMoney(federalAnnualTax, currency)} />
                <Stat compact dark={dark} label="State/year" value={formatMoney(stateAnnualTax, currency)} />
              </div>
            </div>
            {/* Full-width Your Year tile */}
            {(() => {
              const pct = Math.max(0, Math.min(1, effectiveTaxRate || 0));
              const redSquares = Math.round(pct * 365);
              const squares = Array.from({ length: 365 }, (_, i) => i);
              return (
                <div style={{ ...S.statBox(dark), marginTop: 8 }}>
                  <div style={{ ...S.muted(dark), textTransform: 'uppercase', letterSpacing: 0.9, marginBottom: 10 }}>Your Year</div>
                  <div ref={taxGridWrapRef} style={{ width: '100%', padding: (typeof window !== 'undefined' && window.innerWidth < 640) ? 8 : 8, boxSizing: 'border-box', overflow: 'hidden', borderRadius: 2, border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`, background: dark ? 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.015))' : 'linear-gradient(180deg, rgba(0,0,0,0.035), rgba(0,0,0,0.015))', boxShadow: dark ? '0 10px 24px rgba(0,0,0,0.45)' : '0 10px 24px rgba(0,0,0,0.15)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${taxCols}, minmax(0, 1fr))`, gap: (typeof window !== 'undefined' && window.innerWidth < 640) ? 2 : 2, width: '100%' }}>
                      {squares.map(i => (
                        <div key={i} style={{ aspectRatio: '1 / 1', borderRadius: 2, background: i < redSquares ? '#ff3b30' : (dark ? '#3a3a3a' : '#cfcfcf'), boxShadow: i < redSquares ? (dark ? '0 0 6px rgba(255,59,48,0.5)' : '0 0 6px rgba(255,59,48,0.35)') : 'none' }} />
                      ))}
                    </div>
                  </div>
                  <div style={{ marginTop: 12, fontSize: 26, lineHeight: 1.25, color: dark ? '#eaeaea' : '#111', fontWeight: 600 }}>
                    <span style={{ fontWeight: 800, color: '#ff3b30' }}>{redSquares}</span> workdays each year go to taxes.
                  </div>
                </div>
              );
            })()}
            <div style={{ marginTop: 14, marginBottom: -6, ...S.muted(dark), fontSize: 11, opacity: 0.6, textAlign: 'center' }}>
              Disclaimer: This is 100% vibe coded. The math is probably wrong. Don't use these numbers for anything important.
            </div>
          </section>
        </div>
      </main>

      {/* Gift Modal */}
      {giftOpen && (
        <div onClick={() => setGiftOpen(false)} style={{
          position: "fixed", inset: 0,
          background: dark ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 50
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            ...S.card(dark),
            padding: 24, width: "min(440px, 92vw)",
            backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.35)'
          }}>
            <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Gift a Minute</div>
            <div style={{ ...S.muted(dark), marginBottom: 12 }}>
              Send your per-minute amount to <span style={{ fontWeight: 600 }}>${CASH_TAG}</span> via Cash App (USD).
            </div>
            <div>
              <FieldLabel dark={dark}>Amount (USD)</FieldLabel>
              <div style={{ position: "relative", marginBottom: 12 }}>
                <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: dark ? "#aaa" : "#666" }}>$</span>
                <input
                  id="gift-amount"
                  inputMode="decimal"
                  type="text"
                  value={giftAmount}
                  onChange={(e) => setGiftAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                  style={{ ...S.input(dark), paddingLeft: 24 }}
                />
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button
                onClick={() => setGiftOpen(false)}
                onMouseEnter={(e)=>{ e.currentTarget.style.background = dark? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.background = 'transparent'; }}
                style={{ ...S.outlineBtn(dark) }}>Cancel</button>
              <button
                onClick={handleGiftCopy}
                onMouseEnter={(e)=>{ e.currentTarget.style.background = dark? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)'; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.background = 'transparent'; }}
                style={{ ...S.outlineBtn(dark) }}>Copy link</button>
              <button
                onClick={handleGiftSend}
                onMouseEnter={(e)=>{ e.currentTarget.style.background = 'linear-gradient(180deg, rgba(0,219,52,0.26), rgba(0,219,52,0.18))'; }}
                onMouseLeave={(e)=>{ e.currentTarget.style.background = 'linear-gradient(180deg, rgba(0,219,52,0.20), rgba(0,219,52,0.12))'; }}
                style={{ ...S.presetBtn(dark, true), background: 'linear-gradient(180deg, rgba(0,219,52,0.20), rgba(0,219,52,0.12))', height: 36 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <HeartIcon size={16} />
                  <span style={S.presetLabel(dark)}>Send via Cash App</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <footer style={{ padding: "32px 16px", textAlign: "center", ...S.muted(dark) }}>
        Built with gpt-5 and and $0 of my time (i'm unemployed).
      </footer>
    </div>
  );
}

